import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Footer } from './Footer';
import {
  Users,
  Mail,
  CheckCircle,
  XCircle,
  Plus,
  Send,
  Eye,
  Loader2,
  AlertCircle,
  Filter,
  Trash2,
  RotateCcw,
  User,
  MessageSquare,
  Download,
  Check,
  X,
  FileText
} from 'lucide-react';
import { apiUrl } from '../config/api';

interface AdminUser {
  id: string;
  email: string;
  fullName: string;
  company: string;
  role: string;
  status: string;
}

interface AdminPanelProps {
  user: AdminUser;
  onLogout: () => void;
}

interface Team {
  _id: string;
  companyName: string;
  contactName: string;
  contactEmail: string;
  teamEmails: string[];
  message?: string;
  status: 'pending' | 'approved' | 'rejected';
  createdAt: string;
  approvedAt?: string;
  rejectedAt?: string;
  rejectionReason?: string;
}

interface Invitation {
  id: string;
  email: string;
  token: string;
  used: boolean;
  createdAt: string;
  expiresAt: string;
  usedAt?: string;
  lastAccessedAt?: string | null;
  createdBy?: {
    id: string;
    fullName: string;
    email: string;
  } | null;
  user?: {
    id: string;
    email: string;
    fullName?: string;
    status?: string;
    role?: string;
    userType?: string;
    module1Completed?: boolean;
    module1CompletedAt?: string | null;
  } | null;
}

interface IndividualInvitationRecord {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  fullName: string;
  position?: string;
  company?: string;
  status: string;
  sentAt: string;
  createdBy?: {
    id: string;
    fullName: string;
    email: string;
  } | null;
}

interface UserData {
  id: string;
  email: string;
  fullName?: string;
  company?: string;
  role: string;
  userType: string;
  status: string;
  socialProvider?: string;
  socialId?: string;
  userSource?: string;
  invitedBy?: {
    id: string;
    name: string;
  } | null;
  fieldOfWork?: string;
  recruiterType?: string;
  teamSize?: string;
  module1Completed?: boolean;
  module1CompletedAt?: string | null;
  createdAt: string;
  updatedAt: string;
}

interface FeedbackSubmission {
  id: string;
  userEmail: string;
  moduleId: number;
  submittedAt: string;
  isRead?: boolean;
  readAt?: string;
  module1ValueRating?: string;
  module2ValueRating?: string;
  module3ValueRating?: string;
  actionableInsights?: string;
  usefulTakeaway?: string;
  fieldOfWork?: string;
  upgradeInterest?: string;
  upgradePriceRange?: string;
  contentFormatPreference?: string;
  trainerConnectionInterest?: string;
  trainerConnectionType?: string;
  discoverySource?: string;
  recommendationScore?: string;
  additionalThoughts?: string;
  courseSatisfaction?: string;
  pdfHelpfulness?: string;
  implementationConfidence?: string;
  feedbackTimeliness?: string;
  feedbackQuality?: string;
  feedbackApplicability?: string;
  highlight?: string;
  improvementIdeas?: string;
  followUpInterest?: string;
  followUpContact?: string;
  feedbackVersion?: string;
}

function getUserSourceBadge(source?: string): { label: string; background: string } | null {
  if (!source) return null;
  
  switch (source) {
    case 'unknown_email':
    case 'new_email':
      return { label: 'NEW_EMAIL', background: '#2563eb' };
    case 'prior_full_request':
    case 'waiting_list_interest':
    case 'known_email':
      return { label: 'KNOWN_EMAIL', background: '#7c3aed' };
    case 'admin_invitation':
      return { label: 'ADMIN INVITED', background: '#059669' };
    case 'individual_invitation':
      return { label: 'INDIVIDUAL INVITED', background: '#0ea5e9' };
    case 'team_invitation':
      return { label: 'TEAM INVITED', background: '#f97316' };
    case 'demo_conversion':
      return { label: 'DEMO CONVERSION', background: '#ef4444' };
    case 'admin_setup':
      return { label: 'ADMIN SETUP', background: '#059669' };
    default:
      return { label: source.toUpperCase(), background: '#6b7280' };
  }
}

export function AdminPanel({ user, onLogout }: AdminPanelProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabFromUrl = searchParams.get('tab');
  const initialTab = (tabFromUrl && ['teams', 'invitations', 'send-individual-invites', 'users', 'feedback', 'account'].includes(tabFromUrl)) 
    ? tabFromUrl as 'teams' | 'invitations' | 'send-individual-invites' | 'users' | 'feedback' | 'account'
    : 'teams';
  const [activeTab, setActiveTab] = useState<'teams' | 'invitations' | 'send-individual-invites' | 'users' | 'feedback' | 'account'>(initialTab);
  const [teams, setTeams] = useState<Team[]>([]);
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [individualInvitations, setIndividualInvitations] = useState<IndividualInvitationRecord[]>([]);
  const [users, setUsers] = useState<UserData[]>([]);
  const [teamMembers, setTeamMembers] = useState<any[]>([]);
  const [feedbacks, setFeedbacks] = useState<FeedbackSubmission[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [teamFilter, setTeamFilter] = useState<'all' | 'pending' | 'approved' | 'rejected'>('all');
  const [inviteFilter, setInviteFilter] = useState<'all' | 'active' | 'used' | 'expired'>('all');
  const [userFilter, setUserFilter] = useState<'all' | 'full' | 'admin'>('all');
  const [selectedUsers, setSelectedUsers] = useState<Set<string>>(new Set());
  const [bulkDeleteLoading, setBulkDeleteLoading] = useState(false);
  
  // Feedback states
  const [selectedFeedback, setSelectedFeedback] = useState<FeedbackSubmission | null>(null);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [feedbackFilter, setFeedbackFilter] = useState<'all' | 'read' | 'unread'>('all');
  
  // Individual invitation states
  const [individualForm, setIndividualForm] = useState({
    firstName: '',
    lastName: '',
    position: '',
    company: '',
    email: ''
  });
  const [sendingIndividualInvite, setSendingIndividualInvite] = useState(false);
  const [individualInviteResult, setIndividualInviteResult] = useState<any>(null);
  
  // Dashboard statistics states
  const [dashboardStats, setDashboardStats] = useState({
    totalUsers: 0,
    activeUsers: 0,
    pendingUsers: 0,
    waitlistEntries: 0,
    teamInvitations: 0,
    individualInvitations: 0,
    totalInvitations: 0,
    manualActiveAccounts: 0,
    manualModule1Completed: 0,
    manualAwaitingActivation: 0
  });
  const manualActiveUsers = dashboardStats.manualActiveAccounts;
  const manualCourseStarts = dashboardStats.manualModule1Completed;
  const manualAwaitingActivation = dashboardStats.manualAwaitingActivation;
  const [profileForm, setProfileForm] = useState({
    fullName: user?.fullName || '',
    company: user?.company || ''
  });
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileSuccess, setProfileSuccess] = useState<string | null>(null);
  const [profileError, setProfileError] = useState<string | null>(null);
  const getAuthHeaders = useCallback(() => ({
    'Authorization': `Bearer ${typeof window !== 'undefined' ? localStorage.getItem('authToken') : ''}`,
    'Content-Type': 'application/json'
  }), []);

  const fetchTeams = useCallback(async () => {
    try {
      const token = typeof window !== 'undefined' ? localStorage.getItem('authToken') : null;
      if (!token) {
        setError('Authentication required');
        return;
      }
      const status = teamFilter === 'all' ? '' : teamFilter;
      console.log('Fetching teams with filter:', { teamFilter, status });
      const response = await fetch(apiUrl(`/api/teams?status=${status}&limit=50&_t=${Date.now()}`), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache'
        }
      });
      const data = await response.json();
      if (data.success) {
        console.log('🔍 FETCH TEAMS - Raw API response:', data);
        console.log('🔍 FETCH TEAMS - Teams data:', data.data.teams);
        setTeams(data.data.teams);
        setError(null);
        console.log('Teams fetched successfully:', data.data.teams);
      } else {
        setError(data.message);
        if (data.message.includes('token') || data.message.includes('auth') || response.status === 401) {
          localStorage.removeItem('authToken');
          window.location.href = '/auth';
        }
      }
    } catch (error) {
      console.error('Error fetching teams:', error);
      setError('Failed to fetch teams');
    }
  }, [teamFilter]);

  const fetchInvitations = useCallback(async () => {
    try {
      const token = typeof window !== 'undefined' ? localStorage.getItem('authToken') : null;
      if (!token) {
        setError('Authentication required');
        return;
      }
      const status = inviteFilter === 'all' ? '' : inviteFilter;
      const response = await fetch(apiUrl(`/api/invitations?status=${status}&limit=500&_t=${Date.now()}`), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache'
        }
      });
      const data = await response.json();
      if (data.success) {
        setInvitations(data.data.invitations || []);
        setError(null);
      } else {
        setError(data.message);
        if (data.message.includes('token') || data.message.includes('auth') || response.status === 401) {
          localStorage.removeItem('authToken');
          window.location.href = '/auth';
        }
      }
    } catch (error) {
      console.error('Error fetching invitations:', error);
      setError('Failed to fetch invitations');
    }
  }, [inviteFilter]);

  const fetchUsers = useCallback(async () => {
    try {
      const token = typeof window !== 'undefined' ? localStorage.getItem('authToken') : null;
      if (!token) {
        setError('Authentication required');
        return;
      }
      const userType = userFilter === 'all' ? '' : userFilter;
      const url = apiUrl(`/api/auth/users?userType=${userType}&_t=${Date.now()}`);
      console.log('🔍 Frontend - Fetching users from:', url);
      console.log('🔍 Frontend - User filter:', userFilter, 'userType:', userType);
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache'
        }
      });
      
      console.log('🔍 Frontend - Response status:', response.status);
      const data = await response.json();
      console.log('🔍 Frontend - Response data:', data);
      
      if (data.success) {
        console.log('🔍 Frontend - Users received:', data.data.users.length, 'users');
        setUsers(data.data.users);
        setError(null);
      } else {
        console.error('🔍 Frontend - API error:', data.message);
        setError(data.message);
        if (data.message.includes('token') || data.message.includes('auth') || response.status === 401) {
          localStorage.removeItem('authToken');
          window.location.href = '/auth';
        }
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      setError('Failed to fetch users');
    }
  }, [userFilter]);

  const fetchTeamMembers = useCallback(async () => {
    try {
      const token = typeof window !== 'undefined' ? localStorage.getItem('authToken') : null;
      if (!token) {
        setError('Authentication required');
        return;
      }
      
      const response = await fetch(apiUrl(`/api/team-members?_t=${Date.now()}`), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache'
        }
      });
      
      const data = await response.json();
      if (data.success) {
        console.log('🔍 FETCH TEAM MEMBERS - API response:', data);
        setTeamMembers(data.data.teamMembers);
      } else {
        console.error('Failed to fetch team members:', data.message);
        setError(data.message || 'Failed to fetch team members');
      }
    } catch (error) {
      console.error('Error fetching team members:', error);
      setError('Failed to fetch team members');
    }
  }, []);

  useEffect(() => {
    setProfileForm({
      fullName: user?.fullName || '',
      company: user?.company || ''
    });
  }, [user?.fullName, user?.company]);

  const fetchFeedbacks = useCallback(async () => {
    try {
      const token = typeof window !== 'undefined' ? localStorage.getItem('authToken') : null;
      if (!token) {
        setError('Authentication required');
        return;
      }
      
      const response = await fetch(apiUrl(`/api/module3-feedback?_t=${Date.now()}`), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache'
        }
      });
      
      const data = await response.json();
      if (data.success) {
        setFeedbacks(data.data.feedbacks || []);
        setError(null);
      } else {
        setError(data.message || 'Failed to fetch feedback');
      }
    } catch (error) {
      console.error('Error fetching feedback:', error);
      setError('Failed to fetch feedback');
    }
  }, []);

  const fetchAdminProfile = useCallback(async () => {
    try {
      setProfileLoading(true);
      const token = typeof window !== 'undefined' ? localStorage.getItem('authToken') : null;
      if (!token) {
        setProfileError('Authentication required');
        setProfileLoading(false);
        return;
      }

      const response = await fetch(apiUrl(`/api/auth/profile?_t=${Date.now()}`), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache'
        }
      });

      const data = await response.json();
      if (data.success) {
        setProfileForm({
          fullName: data.data.fullName || '',
          company: data.data.company || ''
        });
        setProfileError(null);
      } else {
        setProfileError(data.message || 'Failed to load profile');
      }
    } catch (err) {
      console.error('Error fetching admin profile:', err);
      setProfileError('Failed to load profile');
    } finally {
      setProfileLoading(false);
    }
  }, []);

  const handleProfileSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setProfileSaving(true);
    setProfileSuccess(null);
    setProfileError(null);

    try {
      const token = typeof window !== 'undefined' ? localStorage.getItem('authToken') : null;
      if (!token) {
        setProfileError('Authentication required');
        setProfileSaving(false);
        return;
      }

      const response = await fetch(apiUrl('/api/auth/profile'), {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          fullName: profileForm.fullName.trim(),
          company: profileForm.company.trim()
        })
      });

      const data = await response.json();
      if (data.success) {
        const updated = {
          fullName: data.data.fullName || '',
          company: data.data.company || ''
        };
        setProfileForm(updated);
        setProfileSuccess('Profile updated successfully');
        window.dispatchEvent(new CustomEvent('userProfileUpdated', { detail: updated }));
      } else {
        setProfileError(data.message || 'Failed to update profile');
      }
    } catch (err) {
      console.error('Error updating admin profile:', err);
      setProfileError('Failed to update profile');
    } finally {
      setProfileSaving(false);
    }
  };

  const handleMarkAsRead = async (feedbackId: string, isRead: boolean) => {
    try {
      const token = typeof window !== 'undefined' ? localStorage.getItem('authToken') : null;
      if (!token) return;

      setActionLoading(feedbackId);
      const response = await fetch(apiUrl('/api/module3-feedback'), {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ id: feedbackId, isRead: !isRead })
      });

      const data = await response.json();
      if (data.success) {
        await fetchFeedbacks();
      } else {
        setError(data.message || 'Failed to update feedback');
      }
    } catch (error) {
      console.error('Error updating feedback:', error);
      setError('Failed to update feedback');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeleteFeedback = async (feedbackId: string) => {
    if (!window.confirm('Are you sure you want to delete this feedback? This action cannot be undone.')) {
      return;
    }

    try {
      const token = typeof window !== 'undefined' ? localStorage.getItem('authToken') : null;
      if (!token) return;

      setActionLoading(feedbackId);
      const response = await fetch(apiUrl(`/api/module3-feedback?id=${feedbackId}`), {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();
      if (data.success) {
        await fetchFeedbacks();
      } else {
        setError(data.message || 'Failed to delete feedback');
      }
    } catch (error) {
      console.error('Error deleting feedback:', error);
      setError('Failed to delete feedback');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDownloadCSV = async () => {
    try {
      const token = typeof window !== 'undefined' ? localStorage.getItem('authToken') : null;
      if (!token) return;

      const response = await fetch(apiUrl('/api/module3-feedback?format=csv'), {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `feedback-${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Error downloading CSV:', error);
      setError('Failed to download CSV');
    }
  };

  const handleDownloadPDF = async () => {
    try {
      const token = typeof window !== 'undefined' ? localStorage.getItem('authToken') : null;
      if (!token) return;

      const response = await fetch(apiUrl('/api/module3-feedback?format=pdf'), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();
      if (data.success && data.data.feedbacks) {
        // Generate PDF using browser print functionality
        const printWindow = window.open('', '_blank');
        if (!printWindow) {
          setError('Please allow popups to generate PDF');
          return;
        }

        const feedbacks = data.data.feedbacks;
        let html = `
          <!DOCTYPE html>
          <html>
          <head>
            <title>Feedback Report</title>
            <style>
              body { font-family: Arial, sans-serif; padding: 20px; }
              h1 { color: #010206; }
              table { width: 100%; border-collapse: collapse; margin-top: 20px; }
              th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
              th { background-color: #D8B725; color: #010206; }
              tr:nth-child(even) { background-color: #f9fafb; }
            </style>
          </head>
          <body>
            <h1>Course Feedback Report</h1>
            <p>Generated: ${new Date().toLocaleString()}</p>
            <table>
              <thead>
                <tr>
                  <th>User Email</th>
                  <th>Submitted</th>
                  <th>Course Rating</th>
                  <th>PDF Helpfulness</th>
                  <th>Implementation Confidence</th>
                  <th>Feedback Timeliness</th>
                  <th>Feedback Quality</th>
                  <th>Applicability</th>
                  <th>Follow Up</th>
                </tr>
              </thead>
              <tbody>
        `;

        feedbacks.forEach((fb: FeedbackSubmission) => {
          html += `
            <tr>
              <td>${fb.userEmail || 'Anonymous'}</td>
              <td>${new Date(fb.submittedAt).toLocaleString()}</td>
              <td>${fb.courseSatisfaction || fb.module1ValueRating || '-'}</td>
              <td>${fb.pdfHelpfulness || fb.module2ValueRating || '-'}</td>
              <td>${fb.implementationConfidence || fb.module3ValueRating || '-'}</td>
              <td>${fb.feedbackTimeliness || '-'}</td>
              <td>${fb.feedbackQuality || '-'}</td>
              <td>${fb.feedbackApplicability || '-'}</td>
              <td>${fb.followUpInterest === 'Yes' ? (fb.followUpContact || 'Requested') : 'No'}</td>
            </tr>
          `;
        });

        html += `
              </tbody>
            </table>
          </body>
          </html>
        `;

        printWindow.document.write(html);
        printWindow.document.close();
        printWindow.print();
      }
    } catch (error) {
      console.error('Error downloading PDF:', error);
      setError('Failed to download PDF');
    }
  };

  const filteredFeedbacks = feedbacks.filter(fb => {
    if (feedbackFilter === 'read') return fb.isRead === true;
    if (feedbackFilter === 'unread') return !fb.isRead;
    return true;
  });

  const fetchDashboardStats = useCallback(async () => {
    try {
      const token = typeof window !== 'undefined' ? localStorage.getItem('authToken') : null;
      if (!token) {
        setError('Authentication required');
        return;
      }

      // Fetch all data in parallel
      const [usersResponse, invitationsResponse, individualInvitationsResponse, waitlistResponse] = await Promise.all([
        fetch(apiUrl('/api/auth/users?userType=all&_t=' + Date.now()), {
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
        }),
        fetch(apiUrl('/api/invitations?status=all&_t=' + Date.now()), {
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
        }),
        fetch(apiUrl('/api/invitations/individual?_t=' + Date.now()), {
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
        }),
        fetch(apiUrl('/api/waitlist?status=all&_t=' + Date.now()), {
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
        })
      ]);

      const [usersData, invitationsData, individualInvitationsData, waitlistData] = await Promise.all([
        usersResponse.json(),
        invitationsResponse.json(),
        individualInvitationsResponse.json(),
        waitlistResponse.json()
      ]);

      const totalUsers = usersData.success ? usersData.data.users.length : 0;
      const invitationRecords = invitationsData.success
        ? (invitationsData.data?.invitations ?? [])
        : [];

      if (invitationsData.success && Array.isArray(invitationRecords)) {
        setInvitations(invitationRecords);
      }

      const teamInvitations = invitationsData.success
        ? (invitationsData.data?.pagination?.totalItems ?? invitationRecords.length)
        : 0;

      if (individualInvitationsData.success && Array.isArray(individualInvitationsData.data?.invitations)) {
        setIndividualInvitations(individualInvitationsData.data.invitations);
      }

      const individualInvitationsCount = individualInvitationsData.success
        ? (individualInvitationsData.data?.count ?? individualInvitationsData.data?.invitations?.length ?? 0)
        : 0;

      const totalInvitations = teamInvitations + individualInvitationsCount;
      const waitlistEntries = waitlistData.success ? waitlistData.data.entries.length : 0;
      
      const manualActiveAccounts = invitationRecords.filter((invite: any) => invite.user?.status === 'active').length;
      const manualModule1Completed = invitationRecords.filter((invite: any) => invite.user?.module1Completed).length;
      const manualAwaitingActivation = invitationRecords.filter((invite: any) => (invite.user?.status || 'pending') !== 'active').length;
      
      // Calculate active vs pending users
      const activeUsers = usersData.success ? 
        usersData.data.users.filter((user: any) => user.status === 'active').length : 0;
      const pendingUsers = usersData.success ? 
        usersData.data.users.filter((user: any) => user.status === 'pending').length : 0;

      setDashboardStats({
        totalUsers,
        activeUsers,
        pendingUsers,
        waitlistEntries,
        teamInvitations,
        individualInvitations: individualInvitationsCount,
        totalInvitations,
        manualActiveAccounts,
        manualModule1Completed,
        manualAwaitingActivation
      });

      console.log('📊 Dashboard stats updated:', {
        totalUsers,
        teamInvitations,
        individualInvitations: individualInvitationsCount,
        totalInvitations,
        waitlistEntries,
        activeUsers,
        pendingUsers,
        manualActiveAccounts,
        manualModule1Completed,
        manualAwaitingActivation
      });

    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
    }
  }, []);

  const deleteInvitation = async (invitationId: string) => {
    if (!window.confirm('Are you sure you want to delete this invitation? This action cannot be undone.')) {
      return;
    }
    try {
      const response = await fetch(apiUrl(`/api/invitations/${invitationId}`), {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      const data = await response.json();
      if (data.success) {
        alert('Invitation deleted successfully!');
        fetchInvitations();
      } else {
        alert('Failed to delete invitation: ' + data.message);
      }
    } catch (error) {
      console.error('Error deleting invitation:', error);
      alert('Failed to delete invitation');
    }
  };

  const renewInvitation = async (invitationId: string, days: number = 7) => {
    try {
      const response = await fetch(apiUrl(`/api/invitations/${invitationId}/renew`), {
        method: 'PUT',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ days })
      });
      const data = await response.json();
      if (data.success) {
        alert(`Invitation renewed successfully! New expiration: ${new Date(data.data.invitation.expiresAt).toLocaleDateString()}`);
        fetchInvitations();
      } else {
        alert('Failed to renew invitation: ' + data.message);
      }
    } catch (error) {
      console.error('Error renewing invitation:', error);
      alert('Failed to renew invitation');
    }
  };

  const deleteUser = async (userId: string, email: string) => {
    if (!window.confirm(`Are you sure you want to delete user ${email}? This action cannot be undone.`)) {
      return;
    }
    try {
      const response = await fetch(apiUrl('/api/auth/users'), {
        method: 'DELETE',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ userIds: [userId] })
      });

      // Check if response is OK before parsing JSON
      if (!response.ok) {
        if (response.status === 404) {
          throw new Error('API endpoint not found');
        }
        const errorText = await response.text();
        let errorMessage = `Failed to delete user: ${response.status} ${response.statusText}`;
        try {
          const errorData = JSON.parse(errorText);
          errorMessage = errorData.message || errorMessage;
        } catch {
          // If error text is not JSON, use the text as message
          if (errorText) {
            errorMessage = errorText;
          }
        }
        throw new Error(errorMessage);
      }

      const data = await response.json();
      if (data.success) {
        alert(`User ${email} deleted successfully!`);
        fetchUsers();
      } else {
        alert('Failed to delete user: ' + (data.message || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error deleting user:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      alert('Failed to delete user: ' + errorMessage);
    }
  };

  const deleteUserFromTeam = async (teamId: string, email: string) => {
    if (!window.confirm(`Are you sure you want to delete user ${email}? This will remove them from the team and delete their account. This action cannot be undone.`)) {
      return;
    }
    try {
      const response = await fetch(apiUrl(`/api/teams/${teamId}/delete-user`), {
        method: 'DELETE',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email })
      });
      const data = await response.json();
      if (data.success) {
        alert(`User ${email} deleted successfully!`);
        fetchTeams();
      } else {
        alert('Failed to delete user: ' + data.message);
      }
    } catch (error) {
      console.error('Error deleting user:', error);
      alert('Failed to delete user');
    }
  };

  const deleteTeam = async (teamId: string, teamName: string) => {
    if (!window.confirm(`Are you sure you want to delete the entire team "${teamName}"? This will delete all team members and their accounts. This action cannot be undone.`)) {
      return;
    }
    
    console.log('🔍 DELETE TEAM - Starting deletion:', { teamId, teamName });
    
    try {
      const url = apiUrl(`/api/teams?id=${teamId}`);
      console.log('🔍 DELETE TEAM - URL:', url);
      console.log('🔍 DELETE TEAM - Headers:', getAuthHeaders());
      
      const response = await fetch(url, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      
      console.log('🔍 DELETE TEAM - Response status:', response.status);
      console.log('🔍 DELETE TEAM - Response headers:', response.headers);
      
      if (!response.ok) {
        console.error('❌ DELETE TEAM - Response not OK:', response.status, response.statusText);
        const errorText = await response.text();
        console.error('❌ DELETE TEAM - Error response body:', errorText);
        alert(`Failed to delete team: ${response.status} ${response.statusText}\nResponse: ${errorText}`);
        return;
      }
      
      const responseText = await response.text();
      console.log('🔍 DELETE TEAM - Raw response:', responseText);
      
      let data;
      try {
        data = JSON.parse(responseText);
        console.log('🔍 DELETE TEAM - Parsed data:', data);
      } catch (parseError) {
        console.error('❌ DELETE TEAM - JSON parse error:', parseError);
        alert(`Failed to parse response: ${parseError instanceof Error ? parseError.message : 'Unknown error'}\nResponse: ${responseText}`);
        return;
      }
      
      if (data.success) {
        console.log('✅ DELETE TEAM - Success:', data);
        alert(`Team "${teamName}" deleted successfully!`);
        fetchTeams();
      } else {
        console.error('❌ DELETE TEAM - API returned error:', data);
        alert('Failed to delete team: ' + data.message);
      }
    } catch (error) {
      console.error('❌ DELETE TEAM - Network/other error:', error);
      alert('Failed to delete team: ' + (error instanceof Error ? error.message : 'Unknown error'));
    }
  };

  const toggleUserSelection = (userId: string) => {
    const newSelected = new Set(selectedUsers);
    if (newSelected.has(userId)) {
      newSelected.delete(userId);
    } else {
      newSelected.add(userId);
    }
    setSelectedUsers(newSelected);
  };

  const selectAllUsers = () => {
    const allUserIds = new Set<string>();
    teams.forEach(team => {
      team.teamEmails.forEach(email => {
        allUserIds.add(`${team._id}-${email}`);
      });
    });
    setSelectedUsers(allUserIds);
  };

  const clearSelection = () => {
    setSelectedUsers(new Set());
  };

  const bulkDeleteUsers = async () => {
    console.log('🔍 BULK DELETE - Starting bulk delete');
    console.log('🔍 BULK DELETE - Selected users:', selectedUsers);
    console.log('🔍 BULK DELETE - Selected users size:', selectedUsers.size);
    
    if (selectedUsers.size === 0) {
      alert('Please select users to delete');
      return;
    }
    const userList = Array.from(selectedUsers).map(userId => {
      const [teamId, email] = userId.split('-');
      return { teamId, email };
    });
    
    console.log('🔍 BULK DELETE - User list to delete:', userList);
    
    if (!window.confirm(`Are you sure you want to delete ${selectedUsers.size} users? This action cannot be undone.`)) {
      return;
    }
    setBulkDeleteLoading(true);
    try {
      const url = apiUrl('/api/teams/bulk-delete-users');
      console.log('🔍 BULK DELETE - URL:', url);
      console.log('🔍 BULK DELETE - Headers:', getAuthHeaders());
      console.log('🔍 BULK DELETE - Body:', { users: userList });
      
      const response = await fetch(url, {
        method: 'DELETE',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ users: userList })
      });
      
      console.log('🔍 BULK DELETE - Response status:', response.status);
      console.log('🔍 BULK DELETE - Response headers:', response.headers);
      
      if (!response.ok) {
        console.error('❌ BULK DELETE - Response not OK:', response.status, response.statusText);
        const errorText = await response.text();
        console.error('❌ BULK DELETE - Error response body:', errorText);
        alert(`Failed to delete users: ${response.status} ${response.statusText}\nResponse: ${errorText}`);
        return;
      }
      
      const responseText = await response.text();
      console.log('🔍 BULK DELETE - Raw response:', responseText);
      
      let data;
      try {
        data = JSON.parse(responseText);
        console.log('🔍 BULK DELETE - Parsed data:', data);
      } catch (parseError) {
        console.error('❌ BULK DELETE - JSON parse error:', parseError);
        alert(`Failed to parse response: ${parseError instanceof Error ? parseError.message : 'Unknown error'}\nResponse: ${responseText}`);
        return;
      }
      
      if (data.success) {
        console.log('✅ BULK DELETE - Success:', data);
        alert(`Bulk delete completed! ${data.data.summary.deleted} users deleted, ${data.data.summary.errors} errors.`);
        setSelectedUsers(new Set());
        console.log('🔍 BULK DELETE - Refreshing teams after successful deletion...');
        await fetchTeams();
        console.log('🔍 BULK DELETE - Teams refreshed');
      } else {
        console.error('❌ BULK DELETE - API returned error:', data);
        alert('Failed to delete users: ' + data.message);
      }
    } catch (error) {
      console.error('❌ BULK DELETE - Network/other error:', error);
      alert('Failed to delete users: ' + (error instanceof Error ? error.message : 'Unknown error'));
    } finally {
      setBulkDeleteLoading(false);
    }
  };

  useEffect(() => {
    if (!user || user.role !== 'admin') {
      setLoading(false);
      return;
    }
    const loadData = async () => {
      setLoading(true);
      setError(null);
      try {
        // Always fetch dashboard stats
        await fetchDashboardStats();
        
        if (activeTab === 'teams') {
          await fetchTeams();
        } else if (activeTab === 'invitations') {
          await fetchInvitations();
        } else if (activeTab === 'users') {
          await fetchUsers();
        } else if (activeTab === 'account') {
          await fetchAdminProfile();
          await fetchTeamMembers();
        } else if (activeTab === 'feedback') {
          await fetchFeedbacks();
        }
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [activeTab, fetchTeams, fetchInvitations, fetchUsers, fetchTeamMembers, fetchDashboardStats, fetchAdminProfile, fetchFeedbacks, user]);

  const handleApproveTeam = async (teamId: string) => {
    setActionLoading(teamId);
    try {
      const response = await fetch(apiUrl(`/api/teams?id=${teamId}`), {
        method: 'PUT',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ action: 'approve' })
      });
      const data = await response.json();
      if (data.success) {
        setTeams(teams.map(team =>
          team._id === teamId
            ? { ...team, status: 'approved' as const, approvedAt: new Date().toISOString() }
            : team
        ));
        setError(null); // Clear any previous errors
      } else {
        setError(data.message || 'Failed to approve team');
      }
    } catch (error) {
      console.error('Error approving team:', error);
      setError('Failed to approve team');
    } finally {
      setActionLoading(null);
    }
  };

  const handleRejectTeam = async (teamId: string) => {
    const reason = prompt('Reason for rejection (optional):');
    setActionLoading(teamId);
    try {
      const response = await fetch(apiUrl(`/api/teams?id=${teamId}`), {
        method: 'PUT',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ action: 'reject', rejectionReason: reason })
      });
      const data = await response.json();
      if (data.success) {
        setTeams(teams.map(team =>
          team._id === teamId
            ? { ...team, status: 'rejected' as const, rejectedAt: new Date().toISOString(), rejectionReason: reason || undefined }
            : team
        ));
        setError(null); // Clear any previous errors
      } else {
        setError(data.message || 'Failed to reject team');
      }
    } catch (error) {
      console.error('Error rejecting team:', error);
      setError('Failed to reject team');
    } finally {
      setActionLoading(null);
    }
  };

  const handleSendIndividualInvitation = async () => {
    if (!individualForm.firstName || !individualForm.lastName || !individualForm.email) {
      setError('First name, last name, and email are required');
      return;
    }
    
    setSendingIndividualInvite(true);
    setIndividualInviteResult(null);
    setError(null);
    
    try {
      if (individualInvitations.length === 0) {
        await fetchDashboardStats();
      }

      const alreadyInvited = individualInvitations.find(invite => invite.email.toLowerCase() === individualForm.email.trim().toLowerCase());
      if (alreadyInvited) {
        setError('This person has already received an individual invitation.');
        setSendingIndividualInvite(false);
        return;
      }

      console.log('🔍 Frontend - Sending individual invitation:', individualForm);
      const response = await fetch(apiUrl('/api/invitations/individual'), {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(individualForm)
      });
      
      console.log('🔍 Frontend - Individual invitation response status:', response.status);
      const data = await response.json();
      console.log('🔍 Frontend - Individual invitation response data:', data);
      
      if (data.success) {
        console.log('🔍 Frontend - Individual invitation sent successfully');
        setIndividualInviteResult(data.data);
        setIndividualForm({
          firstName: '',
          lastName: '',
          position: '',
          company: '',
          email: ''
        });
        setError(null);
        await fetchDashboardStats();
      } else {
        setError(data.message);
      }
    } catch (error) {
      console.error('Error sending individual invitation:', error);
      setError('Failed to send individual invitation');
    } finally {
      setSendingIndividualInvite(false);
    }
  };

  const handleDeleteIndividualInvitation = async (invitationId: string, email: string) => {
    if (!window.confirm(`Delete the invitation for ${email}? This will also remove their user account.`)) {
      return;
    }

    try {
      const response = await fetch(apiUrl(`/api/invitations/individual?id=${invitationId}`), {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      const data = await response.json();
      if (data.success) {
        setIndividualInvitations(prev => prev.filter(invite => invite.id !== invitationId));
        await fetchDashboardStats();
        setError(null);
      } else {
        setError(data.message || 'Failed to delete invitation');
      }
    } catch (error) {
      console.error('Error deleting individual invitation:', error);
      setError('Failed to delete invitation');
    }
  };

  const getStatusBadge = (status: string) => {
    const styles = {
      pending: { background: '#fbbf24', color: '#92400e' },
      approved: { background: '#34d399', color: '#065f46' },
      rejected: { background: '#f87171', color: '#991b1b' },
      active: { background: '#3b82f6', color: '#1e40af' },
      used: { background: '#34d399', color: '#065f46' },
      expired: { background: '#9ca3af', color: '#374151' }
    };
    const style = styles[status as keyof typeof styles] || styles.pending;
    return (
      <span style={{
        padding: '0.25rem 0.75rem',
        borderRadius: '999px',
        fontSize: '0.75rem',
        fontWeight: 500,
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        ...style
      }}>
        {status}
      </span>
    );
  };

  return (
    <div style={{
      minHeight: 'calc(100vh - 6rem)',
      background: '#f8f9fa',
      display: 'flex',
      flexDirection: 'column'
    }}>
      <div style={{
        flex: 1,
        maxWidth: '1200px',
        margin: '0 auto',
        width: '100%',
        padding: '2rem 20px 4rem 20px'
      }}>
        <div style={{ marginBottom: '2rem' }}>
          <h1 style={{
            fontSize: 'clamp(1.5rem, 4vw, 2.5rem)',
            fontWeight: 600,
            color: 'var(--dark)',
            marginBottom: '0.5rem',
            fontFamily: 'var(--montserrat)'
          }}>
            Admin Panel
          </h1>
          <p style={{
            color: 'var(--gray)',
            fontSize: 'clamp(0.875rem, 2.5vw, 1.125rem)',
            lineHeight: '1.4'
          }}>
            Manage teams, invitations, and course access
          </p>
          <div style={{ marginTop: '1rem' }}>
            <a
              href="/admin/content"
              style={{
                background: 'var(--orange)',
                color: 'var(--dark)',
                padding: '0.75rem 1.5rem',
                borderRadius: '8px',
                textDecoration: 'none',
                fontWeight: 600,
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.5rem',
                fontFamily: 'var(--montserrat)',
                fontSize: 'clamp(0.875rem, 2vw, 1rem)',
                whiteSpace: 'nowrap'
              }}
            >
              <Plus style={{ width: '16px', height: '16px' }} />
              <span>Manage Course Content</span>
            </a>
          </div>
        </div>

        {/* Platform Overview - Redesigned to match waitlist design */}
        <div style={{ marginBottom: '2rem' }}>
          <h2 style={{ 
            fontSize: '2rem', 
            fontWeight: '700', 
            color: 'var(--dark)', 
            margin: '0 0 0.5rem 0' 
          }}>
            Platform Overview
          </h2>
          <p style={{ 
            color: 'var(--gray)', 
            fontSize: '1.1rem', 
            margin: '0 0 2rem 0' 
          }}>
            Track your platform's growth and user engagement
          </p>

          {/* Stats Cards - Matching waitlist design */}
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
            gap: '1rem', 
            marginBottom: '2rem' 
          }}>
            <div style={{ 
              background: 'white', 
              padding: '1.5rem', 
              borderRadius: '12px', 
              border: '1px solid #eee',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '2rem', fontWeight: '700', color: '#8b5cf6', marginBottom: '0.5rem' }}>
                {dashboardStats.totalUsers}
              </div>
              <div style={{ color: 'var(--gray)', fontSize: '0.9rem' }}>Total Users</div>
            </div>
            <div style={{ 
              background: 'white', 
              padding: '1.5rem', 
              borderRadius: '12px', 
              border: '1px solid #eee',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '2rem', fontWeight: '700', color: '#10b981', marginBottom: '0.5rem' }}>
                {dashboardStats.activeUsers}
              </div>
              <div style={{ color: 'var(--gray)', fontSize: '0.9rem' }}>Active Users</div>
            </div>
            <div style={{ 
              background: 'white', 
              padding: '1.5rem', 
              borderRadius: '12px', 
              border: '1px solid #eee',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '2rem', fontWeight: '700', color: '#ef4444', marginBottom: '0.5rem' }}>
                {dashboardStats.pendingUsers}
              </div>
              <div style={{ color: 'var(--gray)', fontSize: '0.9rem' }}>Pending Users</div>
            </div>
            <div style={{ 
              background: 'white', 
              padding: '1.5rem', 
              borderRadius: '12px', 
              border: '1px solid #eee',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '2rem', fontWeight: '700', color: '#0ea5e9', marginBottom: '0.5rem' }}>
                {dashboardStats.teamInvitations}
              </div>
              <div style={{ color: 'var(--gray)', fontSize: '0.9rem' }}>Team Invitations Sent</div>
            </div>
            <div style={{ 
              background: 'white', 
              padding: '1.5rem', 
              borderRadius: '12px', 
              border: '1px solid #eee',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '2rem', fontWeight: '700', color: '#f59e0b', marginBottom: '0.5rem' }}>
                {dashboardStats.individualInvitations}
              </div>
              <div style={{ color: 'var(--gray)', fontSize: '0.9rem' }}>Individual Invitations</div>
            </div>
            <div style={{ 
              background: 'white', 
              padding: '1.5rem', 
              borderRadius: '12px', 
              border: '1px solid #eee',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '2rem', fontWeight: '700', color: '#22c55e', marginBottom: '0.5rem' }}>
                {manualActiveUsers}
              </div>
              <div style={{ color: 'var(--gray)', fontSize: '0.9rem' }}>Manual Invite Accounts Active</div>
            </div>
            <div style={{ 
              background: 'white', 
              padding: '1.5rem', 
              borderRadius: '12px', 
              border: '1px solid #eee',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '2rem', fontWeight: '700', color: '#fb923c', marginBottom: '0.5rem' }}>
                {manualCourseStarts}
              </div>
              <div style={{ color: 'var(--gray)', fontSize: '0.9rem' }}>Manual Invite Module 1 Completions</div>
            </div>
            <div style={{ 
              background: 'white', 
              padding: '1.5rem', 
              borderRadius: '12px', 
              border: '1px solid #eee',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '2rem', fontWeight: '700', color: '#3b82f6', marginBottom: '0.5rem' }}>
                {dashboardStats.totalInvitations}
              </div>
              <div style={{ color: 'var(--gray)', fontSize: '0.9rem' }}>Total Invitations</div>
            </div>
            <div style={{ 
              background: 'white', 
              padding: '1.5rem', 
              borderRadius: '12px', 
              border: '1px solid #eee',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '2rem', fontWeight: '700', color: '#8b5cf6', marginBottom: '0.5rem' }}>
                {dashboardStats.waitlistEntries}
              </div>
              <div style={{ color: 'var(--gray)', fontSize: '0.9rem' }}>Waitlist Entries</div>
            </div>
          </div>
        </div>

        <div style={{
          background: 'white',
          borderRadius: '12px',
          padding: '1rem',
          marginBottom: '2rem',
          border: '1px solid #eee'
        }}>
          <div style={{
            display: 'flex',
            gap: '0.5rem',
            flexWrap: 'wrap',
            overflowX: 'auto',
            paddingBottom: '0.5rem'
          }}>
            {[
              { tab: 'account', icon: User, label: 'Account' },
              { tab: 'teams', icon: Users, label: 'Teams' },
              { tab: 'invitations', icon: Eye, label: 'Invitations' },
              { tab: 'send-individual-invites', icon: Mail, label: 'Individual' },
              { tab: 'users', icon: Users, label: 'Users' },
              { tab: 'feedback', icon: MessageSquare, label: 'Feedback' }
            ].map(({ tab, icon: Icon, label }) => (
              <button
                key={tab}
                onClick={() => {
                  setActiveTab(tab as any);
                  setSearchParams({ tab });
                }}
                style={{
                  padding: '0.75rem 1rem',
                  border: 'none',
                  borderRadius: '8px',
                  background: activeTab === tab ? 'var(--orange)' : 'transparent',
                  color: activeTab === tab ? 'var(--dark)' : 'var(--gray)',
                  fontWeight: 600,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  fontFamily: 'var(--montserrat)',
                  whiteSpace: 'nowrap',
                  minWidth: 'fit-content',
                  fontSize: '0.875rem'
                }}
              >
                <Icon style={{ width: '16px', height: '16px' }} />
                <span style={{ display: 'inline' }}>{label}</span>
              </button>
            ))}
          </div>
        </div>
        {error && (
          <div style={{
            background: '#fee2e2',
            border: '1px solid #fecaca',
            borderRadius: '8px',
            padding: '1rem',
            marginBottom: '2rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem',
            color: '#991b1b'
          }}>
            <AlertCircle style={{ width: '20px', height: '20px' }} />
            <span>{error}</span>
          </div>
        )}

        {activeTab === 'account' && (
          <div style={{
            background: 'white',
            borderRadius: '12px',
            border: '1px solid #eee',
            overflow: 'hidden'
          }}>
            <div style={{
              padding: '1.5rem',
              borderBottom: '1px solid #eee',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <h2 style={{
                fontSize: '1.25rem',
                fontWeight: 600,
                color: 'var(--dark)',
                fontFamily: 'var(--montserrat)'
              }}>
                My Account
              </h2>
            </div>
            <div style={{ padding: '1.5rem' }}>
              {profileError && (
                <div style={{
                  background: '#fee2e2',
                  border: '1px solid #fecaca',
                  borderRadius: '8px',
                  padding: '1rem',
                  marginBottom: '1rem',
                  color: '#991b1b'
                }}>
                  {profileError}
                </div>
              )}
              {profileSuccess && (
                <div style={{
                  background: '#dcfce7',
                  border: '1px solid #bbf7d0',
                  borderRadius: '8px',
                  padding: '1rem',
                  marginBottom: '1rem',
                  color: '#166534'
                }}>
                  {profileSuccess}
                </div>
              )}
              <div style={{
                display: 'grid',
                gap: '1.5rem',
                maxWidth: '600px'
              }}>
                <div style={{
                  background: '#f8f9fa',
                  borderRadius: '8px',
                  padding: '1.5rem',
                  border: '1px solid #eee'
                }}>
                  <h3 style={{
                    fontSize: '1.1rem',
                    fontWeight: 600,
                    color: 'var(--dark)',
                    marginBottom: '1rem',
                    fontFamily: 'var(--montserrat)'
                  }}>
                    Basic Information
                  </h3>
                  {profileLoading ? (
                    <div style={{ padding: '1rem', color: 'var(--gray)' }}>Loading profile...</div>
                  ) : (
                    <form onSubmit={handleProfileSave} style={{ display: 'grid', gap: '1rem' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                        <label style={{ fontSize: '0.875rem', fontWeight: 500, color: 'var(--gray)' }}>
                          Full Name
                        </label>
                        <input
                          type="text"
                          value={profileForm.fullName}
                          onChange={(e) => setProfileForm(prev => ({ ...prev, fullName: e.target.value }))}
                          placeholder="Enter your full name"
                          style={{
                            padding: '0.75rem',
                            border: '1px solid #ddd',
                            borderRadius: '6px',
                            fontFamily: 'var(--montserrat)'
                          }}
                          disabled={profileSaving}
                        />
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                        <label style={{ fontSize: '0.875rem', fontWeight: 500, color: 'var(--gray)' }}>
                          Email
                        </label>
                        <input
                          type="email"
                          value={user.email}
                          disabled
                          style={{
                            padding: '0.75rem',
                          border: '1px solid #ddd',
                            borderRadius: '6px',
                            background: '#f8f9fa',
                            fontFamily: 'var(--montserrat)'
                          }}
                        />
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                        <label style={{ fontSize: '0.875rem', fontWeight: 500, color: 'var(--gray)' }}>
                          Company
                        </label>
                        <input
                          type="text"
                          value={profileForm.company}
                          onChange={(e) => setProfileForm(prev => ({ ...prev, company: e.target.value }))}
                          placeholder="Enter your company name"
                          style={{
                            padding: '0.75rem',
                            border: '1px solid #ddd',
                            borderRadius: '6px',
                            fontFamily: 'var(--montserrat)'
                          }}
                          disabled={profileSaving}
                        />
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                        <label style={{ fontSize: '0.875rem', fontWeight: 500, color: 'var(--gray)' }}>
                          Role
                        </label>
                        <input
                          type="text"
                          value={user.role}
                          disabled
                          style={{
                            padding: '0.75rem',
                            border: '1px solid #ddd',
                            borderRadius: '6px',
                            background: '#f8f9fa',
                            fontFamily: 'var(--montserrat)'
                          }}
                        />
                      </div>
                      <button
                        type="submit"
                        disabled={profileSaving}
                        style={{
                          background: profileSaving ? '#ccc' : 'var(--orange)',
                          color: profileSaving ? '#666' : 'var(--dark)',
                          border: 'none',
                          padding: '0.85rem 1.75rem',
                          borderRadius: '8px',
                          fontWeight: 600,
                          fontFamily: 'var(--montserrat)',
                          cursor: profileSaving ? 'not-allowed' : 'pointer',
                          justifySelf: 'flex-start',
                          transition: 'all 0.2s ease'
                        }}
                      >
                        {profileSaving ? 'Saving...' : 'Save Changes'}
                      </button>
                    </form>
                  )}
                </div>

                {/* Team Collaboration Section */}
                <div style={{
                  background: '#f8f9fa',
                  borderRadius: '8px',
                  padding: '1.5rem',
                  border: '1px solid #eee'
                }}>
                  <h3 style={{
                    fontSize: '1.1rem',
                    fontWeight: 600,
                    color: 'var(--dark)',
                    marginBottom: '1rem',
                    fontFamily: 'var(--montserrat)'
                  }}>
                    Team Collaboration
                  </h3>
                  {loading ? (
                    <div style={{
                      padding: '2rem',
                      textAlign: 'center',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '1rem'
                    }}>
                      <Loader2 style={{
                        width: '1.5rem',
                        height: '1.5rem',
                        color: 'var(--orange)',
                        animation: 'spin 1s linear infinite'
                      }} />
                      <p style={{ margin: 0, color: 'var(--gray)', fontSize: '0.9rem' }}>
                        Loading team members...
                      </p>
                    </div>
                  ) : teamMembers.length === 0 ? (
                    <div style={{
                      padding: '1rem',
                      background: 'white',
                      borderRadius: '6px',
                      border: '1px solid #ddd',
                      textAlign: 'center',
                      color: 'var(--gray)'
                    }}>
                      <Users style={{ width: '2rem', height: '2rem', margin: '0 auto 0.5rem', opacity: 0.5 }} />
                      <p style={{ margin: 0, fontSize: '0.9rem' }}>
                        No team members found. Invite colleagues to start collaborating!
                      </p>
                    </div>
                  ) : (
                    <div style={{ display: 'grid', gap: '1rem' }}>
                      {teamMembers.map((member, index) => (
                        <div
                          key={member.id || index}
                          style={{
                            background: 'white',
                            borderRadius: '6px',
                            padding: '1rem',
                            border: '1px solid #ddd',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                          }}
                        >
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                            <div style={{
                              fontWeight: 600,
                              color: 'var(--dark)',
                              fontSize: '0.9rem',
                              fontFamily: 'var(--montserrat)'
                            }}>
                              {member.fullName || 'Unknown User'}
                            </div>
                            <div style={{
                              color: 'var(--gray)',
                              fontSize: '0.8rem'
                            }}>
                              {member.email}
                            </div>
                            {member.company && (
                              <div style={{
                                color: 'var(--gray)',
                                fontSize: '0.8rem'
                              }}>
                                {member.company} • {member.role}
                              </div>
                            )}
                          </div>
                          <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'flex-end',
                            gap: '0.25rem'
                          }}>
                            <span style={{
                              fontSize: '0.75rem',
                              padding: '0.25rem 0.5rem',
                              borderRadius: '4px',
                              background: member.relationship === 'inviter' ? '#e3f2fd' : 
                                         member.relationship === 'sibling' ? '#f3e5f5' : '#e8f5e8',
                              color: member.relationship === 'inviter' ? '#1976d2' :
                                     member.relationship === 'sibling' ? '#7b1fa2' : '#388e3c',
                              fontWeight: 500
                            }}>
                              {member.relationship === 'inviter' ? 'Invited you' :
                               member.relationship === 'sibling' ? 'Colleague' : 'You invited'}
                            </span>
                            <span style={{
                              fontSize: '0.7rem',
                              color: 'var(--gray)'
                            }}>
                              Joined {new Date(member.createdAt).toLocaleDateString()}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'teams' && (
          <div style={{
            background: 'white',
            borderRadius: '12px',
            border: '1px solid #eee',
            overflow: 'hidden'
          }}>
            <div style={{
              padding: '1.5rem',
              borderBottom: '1px solid #eee',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <h2 style={{
                fontSize: '1.25rem',
                fontWeight: 600,
                color: 'var(--dark)',
                fontFamily: 'var(--montserrat)'
              }}>
                Team Registrations
              </h2>
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                <Filter style={{ width: '16px', height: '16px', color: 'var(--gray)' }} />
                <select
                  value={teamFilter}
                  onChange={(e) => setTeamFilter(e.target.value as any)}
                  style={{
                    padding: '0.5rem',
                    border: '1px solid #eee',
                    borderRadius: '6px',
                    background: 'white',
                    color: 'var(--dark)',
                    fontFamily: 'var(--montserrat)'
                  }}
                >
                  <option value="all">All Teams</option>
                  <option value="pending">Pending</option>
                  <option value="approved">Approved</option>
                  <option value="rejected">Rejected</option>
                </select>
              </div>
            </div>
            {teams.length > 0 && (
              <div style={{
                padding: '1rem 1.5rem',
                background: '#f8f9fa',
                borderBottom: '1px solid #eee',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                  <input
                    type="checkbox"
                    checked={selectedUsers.size > 0}
                    onChange={() => selectedUsers.size > 0 ? clearSelection() : selectAllUsers()}
                    style={{ width: '16px', height: '16px' }}
                  />
                  <span style={{ fontSize: '0.875rem', color: 'var(--gray)' }}>
                    {selectedUsers.size > 0
                      ? `${selectedUsers.size} user${selectedUsers.size > 1 ? 's' : ''} selected`
                      : 'Select users for bulk delete'
                    }
                  </span>
                </div>
                {selectedUsers.size > 0 && (
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button
                      onClick={clearSelection}
                      style={{
                        background: 'transparent',
                        color: 'var(--gray)',
                        border: '1px solid #ddd',
                        padding: '0.5rem 1rem',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '0.875rem',
                        fontFamily: 'var(--montserrat)'
                      }}
                    >
                      Clear Selection
                    </button>
                    <button
                      onClick={bulkDeleteUsers}
                      disabled={bulkDeleteLoading}
                      style={{
                        background: '#dc2626',
                        color: 'white',
                        border: 'none',
                        padding: '0.5rem 1rem',
                        borderRadius: '6px',
                        cursor: bulkDeleteLoading ? 'not-allowed' : 'pointer',
                        fontSize: '0.875rem',
                        fontWeight: 500,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.25rem',
                        opacity: bulkDeleteLoading ? 0.6 : 1,
                        fontFamily: 'var(--montserrat)'
                      }}
                    >
                      {bulkDeleteLoading ? (
                        <>
                          <Loader2 style={{ width: '14px', height: '14px', animation: 'spin 1s linear infinite' }} />
                          Deleting...
                        </>
                      ) : (
                        <>
                          <Trash2 style={{ width: '14px', height: '14px' }} />
                          Delete Selected ({selectedUsers.size})
                        </>
                      )}
                    </button>
                  </div>
                )}
              </div>
            )}
            {loading ? (
              <div style={{
                padding: '3rem',
                textAlign: 'center',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '1rem'
              }}>
                <Loader2 style={{
                  width: '2rem',
                  height: '2rem',
                  color: 'var(--orange)',
                  animation: 'spin 1s linear infinite'
                }} />
                <p style={{ color: 'var(--gray)' }}>Loading teams...</p>
              </div>
            ) : teams.length === 0 ? (
              <div style={{
                padding: '3rem',
                textAlign: 'center',
                color: 'var(--gray)'
              }}>
                <Users style={{ width: '3rem', height: '3rem', margin: '0 auto 1rem', opacity: 0.5 }} />
                <p>No team registrations found</p>
              </div>
            ) : (
              <div style={{ padding: '1.5rem' }}>
                <div style={{ display: 'grid', gap: '1rem' }}>
                  {teams.map((team) => (
                    <div
                      key={team._id}
                      style={{
                        border: '1px solid #eee',
                        borderRadius: '8px',
                        padding: '1.5rem',
                        background: '#f8f9fa'
                      }}
                    >
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        marginBottom: '1rem',
                        flexWrap: 'wrap',
                        gap: '0.5rem'
                      }}>
                        <div style={{ flex: 1, minWidth: '200px' }}>
                          <h3 style={{
                            fontSize: 'clamp(1rem, 3vw, 1.125rem)',
                            fontWeight: 600,
                            color: 'var(--dark)',
                            marginBottom: '0.25rem',
                            wordBreak: 'break-word'
                          }}>
                            {team.companyName}
                          </h3>
                          <p style={{
                            color: 'var(--gray)',
                            fontSize: 'clamp(0.75rem, 2.5vw, 0.875rem)',
                            wordBreak: 'break-word'
                          }}>
                            Contact: {team.contactName} ({team.contactEmail})
                          </p>
                        </div>
                        <div style={{ flexShrink: 0 }}>
                          {getStatusBadge(team.status)}
                        </div>
                      </div>
                      <div style={{ marginBottom: '1rem' }}>
                        <p style={{ fontSize: '0.875rem', color: 'var(--gray)', marginBottom: '0.5rem' }}>
                          <strong>Team Members ({team.teamEmails.length}):</strong>
                        </p>
                        <div style={{
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: '0.5rem'
                        }}>
                          {team.teamEmails.map((email, index) => {
                            const userId = `${team._id}-${email}`;
                            const isSelected = selectedUsers.has(userId);
                            return (
                              <div
                                key={index}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '0.5rem',
                                  background: isSelected ? '#fef3c7' : 'white',
                                  padding: '0.25rem 0.5rem',
                                  borderRadius: '4px',
                                  border: isSelected ? '1px solid #f59e0b' : '1px solid #eee',
                                  transition: 'all 0.2s ease'
                                }}
                              >
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={() => toggleUserSelection(userId)}
                                  style={{
                                    width: '14px',
                                    height: '14px',
                                    cursor: 'pointer'
                                  }}
                                />
                                <span
                                  style={{
                                    fontSize: '0.75rem',
                                    color: 'var(--gray)',
                                    flex: 1
                                  }}
                                >
                                  {email}
                                </span>
                                <button
                                  onClick={async (e) => {
                                    try {
                                      await navigator.clipboard.writeText(email);
                                      // Show a brief success message
                                      const button = e.target as HTMLElement;
                                      const originalTitle = button.title;
                                      const originalColor = button.style.color;
                                      button.title = 'Copied!';
                                      button.style.color = '#10b981';
                                      setTimeout(() => {
                                        button.title = originalTitle;
                                        button.style.color = originalColor;
                                      }, 2000);
                                    } catch (err) {
                                      console.error('Failed to copy email:', err);
                                      // Fallback for older browsers
                                      const textArea = document.createElement('textarea');
                                      textArea.value = email;
                                      document.body.appendChild(textArea);
                                      textArea.select();
                                      document.execCommand('copy');
                                      document.body.removeChild(textArea);
                                    }
                                  }}
                                  style={{
                                    background: 'none',
                                    border: 'none',
                                    cursor: 'pointer',
                                    padding: '0.125rem',
                                    borderRadius: '2px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    marginRight: '0.25rem'
                                  }}
                                  title="Copy email"
                                >
                                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                                    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                                  </svg>
                                </button>
                                <button
                                  onClick={() => deleteUserFromTeam(team._id, email)}
                                  style={{
                                    background: 'none',
                                    border: 'none',
                                    cursor: 'pointer',
                                    padding: '0.125rem',
                                    borderRadius: '2px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                  }}
                                  title="Delete user"
                                >
                                  <Trash2 size={12} color="#ef4444" />
                                </button>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                      {team.message && (
                        <div style={{ marginBottom: '1rem' }}>
                          <p style={{ fontSize: '0.875rem', color: 'var(--gray)', marginBottom: '0.5rem' }}>
                            <strong>Message:</strong>
                          </p>
                          <p style={{
                            background: 'white',
                            padding: '0.75rem',
                            borderRadius: '6px',
                            fontSize: '0.875rem',
                            color: 'var(--dark)',
                            border: '1px solid #eee'
                          }}>
                            {team.message}
                          </p>
                        </div>
                      )}
                      <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '1rem'
                      }}>
                        <div style={{ fontSize: 'clamp(0.7rem, 2vw, 0.75rem)', color: 'var(--gray)', wordBreak: 'break-word' }}>
                          Registered: {new Date(team.createdAt).toLocaleDateString()}
                          {team.approvedAt && (
                            <span> • Approved: {new Date(team.approvedAt).toLocaleDateString()}</span>
                          )}
                          {team.rejectedAt && (
                            <span> • Rejected: {new Date(team.rejectedAt).toLocaleDateString()}</span>
                          )}
                        </div>
                        <div style={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '0.5rem'
                        }}>
                          {team.status === 'pending' && (
                            <div style={{
                              display: 'flex',
                              gap: '0.5rem',
                              flexWrap: 'wrap'
                            }}>
                              <button
                                onClick={() => handleApproveTeam(team._id)}
                                disabled={actionLoading === team._id}
                                style={{
                                  background: '#10b981',
                                  color: 'white',
                                  border: 'none',
                                  padding: '0.5rem 1rem',
                                  borderRadius: '6px',
                                  cursor: 'pointer',
                                  fontSize: 'clamp(0.7rem, 2vw, 0.875rem)',
                                  fontWeight: 500,
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '0.25rem',
                                  whiteSpace: 'nowrap',
                                  minWidth: 'fit-content'
                                }}
                              >
                                {actionLoading === team._id ? (
                                  <Loader2 style={{ width: '14px', height: '14px', animation: 'spin 1s linear infinite' }} />
                                ) : (
                                  <CheckCircle style={{ width: '14px', height: '14px' }} />
                                )}
                                Approve
                              </button>
                              <button
                                onClick={() => handleRejectTeam(team._id)}
                                disabled={actionLoading === team._id}
                                style={{
                                  background: '#ef4444',
                                  color: 'white',
                                  border: 'none',
                                  padding: '0.5rem 1rem',
                                  borderRadius: '6px',
                                  cursor: 'pointer',
                                  fontSize: 'clamp(0.7rem, 2vw, 0.875rem)',
                                  fontWeight: 500,
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '0.25rem',
                                  whiteSpace: 'nowrap',
                                  minWidth: 'fit-content'
                                }}
                              >
                                <XCircle style={{ width: '14px', height: '14px' }} />
                                Reject
                              </button>
                            </div>
                          )}
                          <button
                            onClick={() => deleteTeam(team._id, team.companyName)}
                            style={{
                              background: '#dc2626',
                              color: 'white',
                              border: 'none',
                              padding: '0.5rem 1rem',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontSize: 'clamp(0.7rem, 2vw, 0.875rem)',
                              fontWeight: 500,
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.25rem',
                              whiteSpace: 'nowrap',
                              alignSelf: 'flex-start'
                            }}
                          >
                            <Trash2 style={{ width: '14px', height: '14px' }} />
                            Delete Team
                          </button>
                        </div>
                      </div>
                      {team.rejectionReason && (
                        <div style={{
                          marginTop: '1rem',
                          padding: '0.75rem',
                          background: '#fee2e2',
                          borderRadius: '6px',
                          border: '1px solid #fecaca'
                        }}>
                          <p style={{ fontSize: '0.875rem', color: '#991b1b' }}>
                            <strong>Rejection reason:</strong> {team.rejectionReason}
                          </p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
        {activeTab === 'users' && (
          <div style={{
            background: 'white',
            borderRadius: '12px',
            border: '1px solid #eee',
            overflow: 'hidden'
          }}>
            <div style={{
              padding: '1.5rem',
              borderBottom: '1px solid #eee',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <h2 style={{
                fontSize: '1.25rem',
                fontWeight: 600,
                color: 'var(--dark)',
                fontFamily: 'var(--montserrat)'
              }}>
                All Users
              </h2>
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                <Filter style={{ width: '16px', height: '16px', color: 'var(--gray)' }} />
                <select
                  value={userFilter}
                  onChange={(e) => setUserFilter(e.target.value as any)}
                  style={{
                    padding: '0.5rem',
                    border: '1px solid #eee',
                    borderRadius: '6px',
                    background: 'white',
                    color: 'var(--dark)',
                    fontFamily: 'var(--montserrat)'
                  }}
                >
                  <option value="all">All Users</option>
                  <option value="full">Full Access Users</option>
                  <option value="admin">Admins</option>
                </select>
              </div>
            </div>
            <div style={{ padding: '1.5rem' }}>
              {loading ? (
                <div style={{ textAlign: 'center', padding: '2rem' }}>
                  <Loader2 style={{ width: '24px', height: '24px', animation: 'spin 1s linear infinite' }} />
                  <p style={{ marginTop: '1rem', color: 'var(--gray)' }}>Loading users...</p>
                </div>
              ) : users.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--gray)' }}>
                  <Users style={{ width: '48px', height: '48px', margin: '0 auto 1rem', opacity: 0.5 }} />
                  <p>No users found</p>
                </div>
              ) : (
                <div style={{ display: 'grid', gap: '1rem' }}>
                  {users.map((user) => {
                    console.log('🔍 Frontend - Rendering user:', user);
                    const userSourceBadge = getUserSourceBadge(user.userSource);
                    return (
                    <div
                      key={user.id}
                      style={{
                        padding: '1rem',
                        border: '1px solid #eee',
                        borderRadius: '8px',
                        background: 'white'
                      }}
                    >
                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                        gap: '1rem',
                        alignItems: 'center'
                      }}>
                        <div>
                          <div style={{ fontWeight: '600', color: 'var(--dark)', marginBottom: '0.25rem' }}>
                            {user.fullName || 'No name'}
                          </div>
                          <div style={{ color: 'var(--gray)', fontSize: '0.875rem' }}>
                            {user.email}
                          </div>
                        </div>
                        <div>
                          <div style={{ color: 'var(--gray)', fontSize: '0.875rem', marginBottom: '0.25rem' }}>
                            {user.company || 'No company'}
                          </div>
                          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                            <span style={{
                              background: user.role === 'admin' ? '#10b981' : '#3b82f6',
                              color: 'white',
                              padding: '0.25rem 0.5rem',
                              borderRadius: '4px',
                              fontSize: '0.75rem',
                              fontWeight: '600'
                            }}>
                              {user.role === 'admin' ? 'ADMIN' : 'USER'}
                            </span>
                            {user.userType && (
                              <span style={{
                                background: '#10b981',
                                color: 'white',
                                padding: '0.25rem 0.5rem',
                                borderRadius: '4px',
                                fontSize: '0.75rem',
                                fontWeight: '600'
                              }}>
                                {user.userType.toUpperCase()} ACCESS
                              </span>
                            )}
                            <span style={{
                              background: user.status === 'active' ? '#10b981' : '#6b7280',
                              color: 'white',
                              padding: '0.25rem 0.5rem',
                              borderRadius: '4px',
                              fontSize: '0.75rem',
                              fontWeight: '600'
                            }}>
                              {user.status.toUpperCase()}
                            </span>
                            {user.fieldOfWork && (
                              <span style={{
                                background: '#D8B725',
                                color: 'var(--dark)',
                                padding: '0.25rem 0.5rem',
                                borderRadius: '4px',
                                fontSize: '0.75rem',
                                fontWeight: '600'
                              }}>
                                {user.fieldOfWork.toUpperCase()}
                              </span>
                            )}
                            {user.socialProvider && (
                              <span style={{
                                background: '#6366f1',
                                color: 'white',
                                padding: '0.25rem 0.5rem',
                                borderRadius: '4px',
                                fontSize: '0.75rem',
                                fontWeight: '600'
                              }}>
                                {user.socialProvider.toUpperCase()}
                              </span>
                            )}
                            {userSourceBadge && (
                              <span style={{
                                background: userSourceBadge.background,
                                color: 'white',
                                padding: '0.25rem 0.5rem',
                                borderRadius: '4px',
                                fontSize: '0.75rem',
                                fontWeight: '600'
                              }}>
                                {userSourceBadge.label}
                              </span>
                            )}
                          </div>
                        </div>
                        <div style={{ color: 'var(--gray)', fontSize: '0.875rem' }}>
                          <div>Created: {new Date(user.createdAt).toLocaleDateString()}</div>
                          <div>Updated: {new Date(user.updatedAt).toLocaleDateString()}</div>
                          {user.module1Completed && (
                            <div style={{ 
                              color: '#10b981', 
                              fontWeight: '600',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.25rem',
                              marginTop: '0.25rem'
                            }}>
                              <CheckCircle style={{ width: '16px', height: '16px' }} />
                              Module 1 Completed
                              {user.module1CompletedAt && (
                                <span style={{ fontSize: '0.75rem', fontWeight: '400' }}>
                                  ({new Date(user.module1CompletedAt).toLocaleDateString()})
                                </span>
                              )}
                            </div>
                          )}
                          {user.invitedBy && (
                            <div style={{ color: '#059669', fontWeight: '500', marginTop: '0.25rem' }}>
                              Invited by: {user.invitedBy.name}
                            </div>
                          )}
                        </div>
                        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                          {user.role !== 'admin' ? (
                            <button
                              onClick={() => {
                                console.log('🔍 Frontend - Delete button clicked for user:', user);
                                deleteUser(user.id, user.email);
                              }}
                              style={{
                                background: '#ef4444',
                                color: 'white',
                                border: 'none',
                                padding: '0.5rem 1rem',
                                borderRadius: '6px',
                                fontSize: '0.875rem',
                                fontWeight: '600',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.25rem'
                              }}
                            >
                              <Trash2 style={{ width: '16px', height: '16px' }} />
                              Delete
                            </button>
                          ) : (
                            <span style={{ color: '#6b7280', fontSize: '0.875rem' }}>
                              Admin user (cannot delete)
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}
        {activeTab === 'send-individual-invites' && (
          <div style={{
            background: 'white',
            borderRadius: '12px',
            border: '1px solid #eee',
            padding: '2rem'
          }}>
            <h2 style={{
              fontSize: 'clamp(1.125rem, 4vw, 1.25rem)',
              fontWeight: 600,
              color: 'var(--dark)',
              marginBottom: '1rem',
              fontFamily: 'var(--montserrat)',
              wordBreak: 'break-word'
            }}>
              Send Individual User Invitation
            </h2>
            <p style={{
              color: 'var(--gray)',
              marginBottom: '1.5rem',
              fontSize: 'clamp(0.875rem, 2.5vw, 1rem)',
              wordBreak: 'break-word'
            }}>
              Send a personalized invitation to an individual user with their name and details.
            </p>
            
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
              <div>
                <label style={{
                  display: 'block',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  color: 'var(--dark)',
                  marginBottom: '0.5rem'
                }}>
                  First Name *
                </label>
                <input
                  type="text"
                  value={individualForm.firstName}
                  onChange={(e) => setIndividualForm(prev => ({ ...prev, firstName: e.target.value }))}
                  placeholder="John"
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #eee',
                    borderRadius: '8px',
                    fontFamily: 'var(--raleway)',
                    fontSize: '0.875rem'
                  }}
                />
              </div>
              
              <div>
                <label style={{
                  display: 'block',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  color: 'var(--dark)',
                  marginBottom: '0.5rem'
                }}>
                  Last Name *
                </label>
                <input
                  type="text"
                  value={individualForm.lastName}
                  onChange={(e) => setIndividualForm(prev => ({ ...prev, lastName: e.target.value }))}
                  placeholder="Doe"
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #eee',
                    borderRadius: '8px',
                    fontFamily: 'var(--raleway)',
                    fontSize: '0.875rem'
                  }}
                />
              </div>
              
              <div>
                <label style={{
                  display: 'block',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  color: 'var(--dark)',
                  marginBottom: '0.5rem'
                }}>
                  Email Address *
                </label>
                <input
                  type="email"
                  value={individualForm.email}
                  onChange={(e) => setIndividualForm(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="john@company.com"
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #eee',
                    borderRadius: '8px',
                    fontFamily: 'var(--raleway)',
                    fontSize: '0.875rem'
                  }}
                />
              </div>
              
              <div>
                <label style={{
                  display: 'block',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  color: 'var(--dark)',
                  marginBottom: '0.5rem'
                }}>
                  Position
                </label>
                <input
                  type="text"
                  value={individualForm.position}
                  onChange={(e) => setIndividualForm(prev => ({ ...prev, position: e.target.value }))}
                  placeholder="Senior Recruiter"
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #eee',
                    borderRadius: '8px',
                    fontFamily: 'var(--raleway)',
                    fontSize: '0.875rem'
                  }}
                />
              </div>
              
              <div>
                <label style={{
                  display: 'block',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  color: 'var(--dark)',
                  marginBottom: '0.5rem'
                }}>
                  Company
                </label>
                <input
                  type="text"
                  value={individualForm.company}
                  onChange={(e) => setIndividualForm(prev => ({ ...prev, company: e.target.value }))}
                  placeholder="Tech Corp"
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #eee',
                    borderRadius: '8px',
                    fontFamily: 'var(--raleway)',
                    fontSize: '0.875rem'
                  }}
                />
              </div>
            </div>
            
            <div style={{
              display: 'flex',
              gap: '1rem',
              alignItems: 'center',
              flexWrap: 'wrap'
            }}>
              <button
                onClick={handleSendIndividualInvitation}
                disabled={sendingIndividualInvite || !individualForm.firstName || !individualForm.lastName || !individualForm.email}
                style={{
                  background: 'var(--orange)',
                  color: 'var(--dark)',
                  border: 'none',
                  padding: 'clamp(0.75rem, 3vw, 1rem) clamp(1.5rem, 5vw, 2rem)',
                  borderRadius: '8px',
                  fontWeight: 600,
                  cursor: sendingIndividualInvite || !individualForm.firstName || !individualForm.lastName || !individualForm.email ? 'not-allowed' : 'pointer',
                  opacity: sendingIndividualInvite || !individualForm.firstName || !individualForm.lastName || !individualForm.email ? 0.6 : 1,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  fontFamily: 'var(--montserrat)',
                  fontSize: 'clamp(0.875rem, 2.5vw, 1rem)',
                  whiteSpace: 'nowrap',
                  minWidth: 'fit-content'
                }}
              >
                {sendingIndividualInvite ? (
                  <>
                    <Loader2 style={{ width: '16px', height: '16px', animation: 'spin 1s linear infinite' }} />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send style={{ width: '16px', height: '16px' }} />
                    Send Individual Invitation
                  </>
                )}
              </button>
            </div>
            
            {individualInviteResult && (
              <div style={{
                marginTop: '2rem',
                padding: '1.5rem',
                background: individualInviteResult.emailSent ? '#f0f9ff' : '#fee2e2',
                border: individualInviteResult.emailSent ? '1px solid #bae6fd' : '1px solid #fecaca',
                borderRadius: '8px'
              }}>
                <h3 style={{
                  fontSize: '1rem',
                  fontWeight: 600,
                  color: 'var(--dark)',
                  marginBottom: '1rem'
                }}>
                  Individual Invitation Result
                </h3>
                <div style={{ display: 'grid', gap: '0.5rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontWeight: 500, color: 'var(--dark)' }}>Recipient:</span>
                    <span style={{ color: 'var(--gray)' }}>{individualInviteResult.user?.fullName} ({individualInviteResult.user?.email})</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontWeight: 500, color: 'var(--dark)' }}>Position:</span>
                    <span style={{ color: 'var(--gray)' }}>{individualInviteResult.user?.position || 'Not specified'}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontWeight: 500, color: 'var(--dark)' }}>Company:</span>
                    <span style={{ color: 'var(--gray)' }}>{individualInviteResult.user?.company || 'Not specified'}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontWeight: 500, color: 'var(--dark)' }}>Email Status:</span>
                    <span style={{ 
                      color: individualInviteResult.emailSent ? '#10b981' : '#ef4444',
                      fontWeight: 600
                    }}>
                      {individualInviteResult.emailSent ? '✅ Sent Successfully' : '❌ Failed to Send'}
                    </span>
                  </div>
                  {individualInviteResult.emailError && (
                    <div style={{ 
                      marginTop: '0.5rem',
                      padding: '0.75rem',
                      background: '#fee2e2',
                      borderRadius: '6px',
                      border: '1px solid #fecaca'
                    }}>
                      <span style={{ fontSize: '0.875rem', color: '#991b1b' }}>
                        Error: {individualInviteResult.emailError}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
        {activeTab === 'invitations' && (
          <div style={{ padding: '2rem', maxWidth: '1200px', margin: '0 auto' }}>
            {/* Header */}
            <div style={{ marginBottom: '2rem' }}>
              <h1 style={{ 
                fontSize: '2rem', 
                fontWeight: '700', 
                color: 'var(--dark)', 
                margin: '0 0 0.5rem 0' 
              }}>
                Invitation Management
              </h1>
              <p style={{ 
                color: 'var(--gray)', 
                fontSize: '1.1rem', 
                margin: '0' 
              }}>
                Manage course invitations and track user access
              </p>
            </div>

            {/* Stats Cards */}
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
              gap: '1rem', 
              marginBottom: '2rem' 
            }}>
              <div style={{ 
                background: 'white', 
                padding: '1.5rem', 
                borderRadius: '12px', 
                border: '1px solid #eee',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '2rem', fontWeight: '700', color: '#0ea5e9', marginBottom: '0.5rem' }}>
                  {dashboardStats.teamInvitations}
                </div>
                <div style={{ color: 'var(--gray)', fontSize: '0.9rem' }}>Manual Invitations Sent</div>
              </div>
              <div style={{ 
                background: 'white', 
                padding: '1.5rem', 
                borderRadius: '12px', 
                border: '1px solid #eee',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '2rem', fontWeight: '700', color: '#10b981', marginBottom: '0.5rem' }}>
                  {manualActiveUsers}
                </div>
                <div style={{ color: 'var(--gray)', fontSize: '0.9rem' }}>Active Accounts</div>
              </div>
              <div style={{ 
                background: 'white', 
                padding: '1.5rem', 
                borderRadius: '12px', 
                border: '1px solid #eee',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '2rem', fontWeight: '700', color: '#f97316', marginBottom: '0.5rem' }}>
                  {manualCourseStarts}
                </div>
                <div style={{ color: 'var(--gray)', fontSize: '0.9rem' }}>Module 1 Completed</div>
              </div>
              <div style={{ 
                background: 'white', 
                padding: '1.5rem', 
                borderRadius: '12px', 
                border: '1px solid #eee',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '2rem', fontWeight: '700', color: '#ef4444', marginBottom: '0.5rem' }}>
                  {manualAwaitingActivation}
                </div>
                <div style={{ color: 'var(--gray)', fontSize: '0.9rem' }}>Awaiting Activation</div>
              </div>
            </div>

            {/* Controls */}
            <div style={{ 
              background: 'white', 
              padding: '1.5rem', 
              borderRadius: '12px', 
              border: '1px solid #eee',
              marginBottom: '2rem',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: '1rem'
            }}>
              <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <Filter size={16} color="var(--gray)" />
                  <select
                    value={inviteFilter}
                    onChange={(e) => setInviteFilter(e.target.value as any)}
                    style={{
                      padding: '0.5rem',
                      border: '1px solid #ddd',
                      borderRadius: '6px',
                      fontSize: '0.9rem'
                    }}
                  >
                    <option value="all">All Invitations</option>
                    <option value="active">Active</option>
                    <option value="used">Used</option>
                    <option value="expired">Expired</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Invitations Table */}
            <div style={{ 
              background: 'white', 
              borderRadius: '12px', 
              border: '1px solid #eee',
              overflow: 'hidden'
            }}>
              {loading ? (
                <div style={{
                  padding: '3rem',
                  textAlign: 'center',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '1rem'
                }}>
                  <Loader2 style={{
                    width: '2rem',
                    height: '2rem',
                    color: 'var(--orange)',
                    animation: 'spin 1s linear infinite'
                  }} />
                  <p style={{ color: 'var(--gray)' }}>Loading invitations...</p>
                </div>
              ) : invitations.length === 0 ? (
                <div style={{
                  padding: '3rem',
                  textAlign: 'center',
                  color: 'var(--gray)'
                }}>
                  <Mail style={{ width: '3rem', height: '3rem', margin: '0 auto 1rem', opacity: 0.5 }} />
                  <div style={{ fontSize: '1.1rem', marginBottom: '0.5rem' }}>No invitations found</div>
                  <div style={{ fontSize: '0.9rem' }}>No invitations have been sent yet.</div>
                </div>
              ) : (
                <div>
                  {/* Table Header */}
                  <div style={{ 
                    display: 'grid', 
                    gridTemplateColumns: '1.8fr 1fr 1.2fr 1fr 1.2fr 1.2fr 110px', 
                    gap: '1rem', 
                    padding: '1rem', 
                    background: '#f8f9fa', 
                    borderBottom: '1px solid #eee',
                    fontWeight: '600',
                    fontSize: '0.9rem',
                    color: 'var(--dark)'
                  }}>
                    <div>Email</div>
                    <div>Invitation Status</div>
                    <div>Invited By</div>
                    <div>Sent On</div>
                    <div>Account Status</div>
                    <div>Course Progress</div>
                    <div>Actions</div>
                  </div>

                  {/* Table Rows */}
                  {invitations.map((invitation) => {
                    const isExpired = new Date() > new Date(invitation.expiresAt);
                    const status = invitation.used ? 'used' : isExpired ? 'expired' : 'active';
                    const invitedBy = invitation.createdBy ? (invitation.createdBy.fullName || invitation.createdBy.email) : '—';
                    const accountStatus = invitation.user?.status ? invitation.user.status : 'pending';
                    const moduleCompleted = invitation.user?.module1Completed;
                    return (
                      <div
                        key={invitation.id}
                        style={{ 
                          display: 'grid', 
                          gridTemplateColumns: '1.8fr 1fr 1.2fr 1fr 1.2fr 1.2fr 110px', 
                          gap: '1rem', 
                          padding: '1rem', 
                          borderBottom: '1px solid #f0f0f0',
                          alignItems: 'center',
                          fontSize: '0.9rem'
                        }}
                      >
                        <div style={{ color: 'var(--dark)', wordBreak: 'break-word' }}>
                          <div style={{ fontWeight: '600' }}>{invitation.email}</div>
                          {invitation.user?.fullName && (
                            <div style={{ color: 'var(--gray)', fontSize: '0.8rem', marginTop: '0.1rem' }}>
                              {invitation.user.fullName}
                            </div>
                          )}
                        </div>
                        <div>
                          {getStatusBadge(status)}
                        </div>
                        <div style={{ color: 'var(--dark)', fontWeight: '500' }}>
                          {invitedBy}
                        </div>
                        <div style={{ color: 'var(--gray)' }}>
                          {new Date(invitation.createdAt).toLocaleDateString()}
                        </div>
                        <div>
                          <span style={{
                            background: accountStatus === 'active' ? '#10b9811a' : '#f59e0b1a',
                            color: accountStatus === 'active' ? '#047857' : '#b45309',
                            padding: '0.25rem 0.5rem',
                            borderRadius: '999px',
                            fontWeight: '600',
                            fontSize: '0.75rem',
                            textTransform: 'uppercase'
                          }}>
                            {accountStatus}
                          </span>
                        </div>
                        <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center', flexWrap: 'wrap' }}>
                          {moduleCompleted ? (
                            <>
                              <CheckCircle style={{ width: '16px', height: '16px', color: '#10b981' }} />
                              <span style={{ color: '#047857', fontWeight: '600' }}>Module 1 Completed</span>
                              {invitation.user?.module1CompletedAt && (
                                <span style={{ color: 'var(--gray)', fontSize: '0.75rem' }}>
                                  {new Date(invitation.user.module1CompletedAt).toLocaleDateString()}
                                </span>
                              )}
                            </>
                          ) : (
                            <span style={{ color: 'var(--gray)', fontStyle: 'italic' }}>Not started yet</span>
                          )}
                        </div>
                        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
                          {status === 'active' && (
                            <button
                              onClick={() => {
                                const inviteUrl = `${window.location.origin}/preview/${invitation.token}`;
                                navigator.clipboard.writeText(inviteUrl);
                                alert('Invitation link copied to clipboard!');
                              }}
                              style={{
                                background: 'var(--orange)',
                                color: 'white',
                                border: 'none',
                                padding: '0.5rem 1rem',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                fontSize: '0.875rem',
                                fontWeight: '600'
                              }}
                            >
                              Copy Link
                            </button>
                          )}
                          {status !== 'used' && (
                            <button
                              onClick={() => renewInvitation(invitation.id, 7)}
                              style={{
                                background: '#10b981',
                                color: 'white',
                                border: 'none',
                                padding: '0.25rem 0.5rem',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                fontSize: '0.8rem',
                                fontWeight: '500',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.25rem'
                              }}
                            >
                              <RotateCcw size={12} />
                              Renew
                            </button>
                          )}
                          <button
                            onClick={() => deleteInvitation(invitation.id)}
                            style={{
                              background: 'none',
                              border: 'none',
                              color: '#ef4444',
                              cursor: 'pointer',
                              padding: '0.25rem',
                              borderRadius: '4px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                            title="Delete Invitation"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Individual Invitations Log */}
            <div style={{
              background: 'white',
              borderRadius: '12px',
              border: '1px solid #eee',
              marginTop: '2.5rem',
              padding: '1.5rem'
            }}>
              <h2 style={{
                fontSize: '1.5rem',
                fontWeight: 700,
                marginBottom: '1rem',
                color: 'var(--dark)'
              }}>
                Individual Invitations Log
              </h2>
              <p style={{ color: 'var(--gray)', marginBottom: '1.5rem' }}>
                Track one-to-one invitations sent via the Individual tab.
              </p>
              {individualInvitations.length === 0 ? (
                <div style={{
                  padding: '2rem',
                  textAlign: 'center',
                  color: 'var(--gray)'
                }}>
                  No individual invitations found yet.
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  {individualInvitations.map((invite) => (
                    <div key={invite.id} style={{
                      border: '1px solid #f0f0f0',
                      borderRadius: '10px',
                      padding: '1rem',
                      display: 'grid',
                      gridTemplateColumns: '1.6fr 1fr 1fr 1fr 60px',
                      gap: '1rem',
                      alignItems: 'center'
                    }}>
                      <div>
                        <div style={{ fontWeight: 600, color: 'var(--dark)' }}>{invite.fullName}</div>
                        <div style={{ color: 'var(--gray)', fontSize: '0.85rem' }}>{invite.email}</div>
                      </div>
                      <div style={{ color: 'var(--gray)' }}>
                        {invite.company || '—'}
                      </div>
                      <div style={{ color: 'var(--gray)' }}>
                        {invite.createdBy?.fullName || invite.createdBy?.email || '—'}
                      </div>
                      <div>
                        <span style={{
                          background: invite.status === 'delivered' ? '#10b9811a' : invite.status === 'failed' ? '#fee2e2' : '#f3f4f6',
                          color: invite.status === 'delivered' ? '#047857' : invite.status === 'failed' ? '#b91c1c' : '#374151',
                          padding: '0.25rem 0.5rem',
                          borderRadius: '999px',
                          fontWeight: 600,
                          fontSize: '0.75rem',
                          textTransform: 'uppercase'
                        }}>
                          {invite.status}
                        </span>
                        <div style={{ color: 'var(--gray)', fontSize: '0.8rem', marginTop: '0.35rem' }}>
                          {new Date(invite.sentAt).toLocaleDateString()}
                        </div>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <button
                          onClick={() => handleDeleteIndividualInvitation(invite.id, invite.email)}
                          style={{
                            background: 'none',
                            border: 'none',
                            color: '#ef4444',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            padding: '0.25rem',
                            borderRadius: '4px'
                          }}
                          title="Delete invitation and user"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
        
        {activeTab === 'feedback' && (
          <div style={{ padding: '2rem', maxWidth: '1400px', margin: '0 auto' }}>
            {/* Header */}
            <div style={{ marginBottom: '2rem', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '1rem' }}>
              <div>
              <h1 style={{ 
                fontSize: '2rem', 
                fontWeight: '700', 
                color: 'var(--dark)', 
                margin: '0 0 0.5rem 0' 
              }}>
                Course Feedback
              </h1>
              <p style={{ 
                color: 'var(--gray)', 
                fontSize: '1.1rem', 
                margin: '0' 
              }}>
                View and manage feedback submissions from users
              </p>
              </div>
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                <button
                  onClick={handleDownloadCSV}
                  style={{
                    background: '#10b981',
                    color: 'white',
                    border: 'none',
                    padding: '0.5rem 1rem',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '0.9rem',
                    fontWeight: '500',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}
                >
                  <Download size={16} />
                  Download CSV
                </button>
                <button
                  onClick={handleDownloadPDF}
                  style={{
                    background: '#ef4444',
                    color: 'white',
                    border: 'none',
                    padding: '0.5rem 1rem',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '0.9rem',
                    fontWeight: '500',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}
                >
                  <FileText size={16} />
                  Download PDF
                </button>
              </div>
            </div>

            {/* Stats and Filter */}
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
              gap: '1rem',
              marginBottom: '2rem'
            }}>
            <div style={{ 
              background: 'white', 
              padding: '1.5rem', 
              borderRadius: '12px', 
              border: '1px solid #e5e7eb',
                textAlign: 'center'
            }}>
              <div style={{ fontSize: '2rem', fontWeight: '700', color: 'var(--dark)', marginBottom: '0.5rem' }}>
                {feedbacks.length}
              </div>
                <div style={{ color: 'var(--gray)', fontSize: '0.9rem' }}>Total</div>
              </div>
              <div style={{ 
                background: 'white', 
                padding: '1.5rem', 
                borderRadius: '12px', 
                border: '1px solid #e5e7eb',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '2rem', fontWeight: '700', color: '#10b981', marginBottom: '0.5rem' }}>
                  {feedbacks.filter(fb => fb.isRead).length}
                </div>
                <div style={{ color: 'var(--gray)', fontSize: '0.9rem' }}>Read</div>
              </div>
              <div style={{ 
                background: 'white', 
                padding: '1.5rem', 
                borderRadius: '12px', 
                border: '1px solid #e5e7eb',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '2rem', fontWeight: '700', color: '#ef4444', marginBottom: '0.5rem' }}>
                  {feedbacks.filter(fb => !fb.isRead).length}
                </div>
                <div style={{ color: 'var(--gray)', fontSize: '0.9rem' }}>Unread</div>
              </div>
            </div>

            {/* Filter */}
            <div style={{ 
              background: 'white', 
              padding: '1rem', 
              borderRadius: '12px', 
              border: '1px solid #e5e7eb',
              marginBottom: '1rem',
              display: 'flex',
              gap: '0.5rem',
              alignItems: 'center'
            }}>
              <Filter size={16} style={{ color: 'var(--gray)' }} />
              <button
                onClick={() => setFeedbackFilter('all')}
                style={{
                  background: feedbackFilter === 'all' ? 'var(--orange)' : 'transparent',
                  color: feedbackFilter === 'all' ? 'var(--dark)' : 'var(--gray)',
                  border: '1px solid #e5e7eb',
                  padding: '0.5rem 1rem',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '0.9rem',
                  fontWeight: feedbackFilter === 'all' ? '600' : '400'
                }}
              >
                All ({feedbacks.length})
              </button>
              <button
                onClick={() => setFeedbackFilter('read')}
                style={{
                  background: feedbackFilter === 'read' ? '#10b981' : 'transparent',
                  color: feedbackFilter === 'read' ? 'white' : 'var(--gray)',
                  border: '1px solid #e5e7eb',
                  padding: '0.5rem 1rem',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '0.9rem',
                  fontWeight: feedbackFilter === 'read' ? '600' : '400'
                }}
              >
                Read ({feedbacks.filter(fb => fb.isRead).length})
              </button>
              <button
                onClick={() => setFeedbackFilter('unread')}
                style={{
                  background: feedbackFilter === 'unread' ? '#ef4444' : 'transparent',
                  color: feedbackFilter === 'unread' ? 'white' : 'var(--gray)',
                  border: '1px solid #e5e7eb',
                  padding: '0.5rem 1rem',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '0.9rem',
                  fontWeight: feedbackFilter === 'unread' ? '600' : '400'
                }}
              >
                Unread ({feedbacks.filter(fb => !fb.isRead).length})
              </button>
            </div>

            {/* Loading State */}
            {loading && (
              <div style={{ textAlign: 'center', padding: '2rem' }}>
                <Loader2 style={{ animation: 'spin 1s linear infinite', width: '32px', height: '32px', color: 'var(--orange)' }} />
              </div>
            )}

            {/* Error State */}
            {error && (
              <div style={{
                background: '#f9fafb',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                padding: '1rem',
                marginBottom: '1rem',
                color: 'var(--gray)'
              }}>
                {error}
              </div>
            )}

            {/* Feedback List */}
            {!loading && filteredFeedbacks.length === 0 && !error && (
              <div style={{
                background: 'white',
                borderRadius: '12px',
                border: '1px solid #eee',
                padding: '3rem',
                textAlign: 'center'
              }}>
                <MessageSquare style={{ width: '48px', height: '48px', color: 'var(--gray)', margin: '0 auto 1rem' }} />
                <p style={{ color: 'var(--gray)', fontSize: '1rem', margin: 0 }}>
                  {feedbackFilter === 'all' ? 'No feedback submissions yet.' : `No ${feedbackFilter} feedback submissions.`}
                </p>
              </div>
            )}

            {!loading && filteredFeedbacks.length > 0 && (
              <div style={{
                background: 'white',
                borderRadius: '12px',
                border: '1px solid #eee',
                overflow: 'hidden'
              }}>
                {/* Table Header */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: '2fr 1.5fr 1fr 1fr 1fr',
                  gap: '1rem',
                  padding: '1rem 1.5rem',
                  background: '#f9fafb',
                  borderBottom: '2px solid #e5e7eb',
                  fontWeight: '600',
                  fontSize: '0.9rem',
                  color: 'var(--dark)'
                }}>
                  <div>User</div>
                  <div>Date</div>
                  <div>Status</div>
                  <div>View</div>
                  <div>Actions</div>
                </div>

                {/* Table Rows */}
                <div style={{ maxHeight: '600px', overflowY: 'auto' }}>
                  {filteredFeedbacks.map((feedback) => (
                    <div
                      key={feedback.id}
                      style={{
                        display: 'grid',
                        gridTemplateColumns: '2fr 1.5fr 1fr 1fr 1fr',
                        gap: '1rem',
                        padding: '1rem 1.5rem',
                        borderBottom: '1px solid #f0f0f0',
                        alignItems: 'center',
                        background: feedback.isRead ? '#f9fafb' : 'white',
                        transition: 'background 0.2s ease',
                        opacity: feedback.isRead ? 0.7 : 1
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = feedback.isRead ? '#f0f0f0' : '#f9fafb';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = feedback.isRead ? '#f9fafb' : 'white';
                      }}
                    >
                      {/* User Email */}
                      <div>
                        <div style={{ 
                          fontWeight: '600', 
                          color: 'var(--dark)', 
                          marginBottom: '0.25rem',
                          fontSize: '0.95rem'
                        }}>
                            {feedback.userEmail || 'Anonymous'}
                          </div>
                        {feedback.isRead && feedback.readAt && (
                          <div style={{ color: 'var(--gray)', fontSize: '0.75rem' }}>
                            Read: {new Date(feedback.readAt).toLocaleDateString()}
                          </div>
                        )}
                      </div>

                      {/* Date */}
                          <div style={{ color: 'var(--gray)', fontSize: '0.9rem' }}>
                        {new Date(feedback.submittedAt).toLocaleString()}
                          </div>

                      {/* Status */}
                      <div>
                        {feedback.isRead ? (
                          <span style={{
                            background: '#10b981',
                            color: 'white',
                            padding: '0.25rem 0.75rem',
                            borderRadius: '12px',
                            fontSize: '0.75rem',
                            fontWeight: '600'
                          }}>
                            Read
                          </span>
                        ) : (
                          <span style={{
                            background: '#ef4444',
                            color: 'white',
                            padding: '0.25rem 0.75rem',
                            borderRadius: '12px',
                            fontSize: '0.75rem',
                            fontWeight: '600'
                          }}>
                            New
                          </span>
                        )}
                        </div>

                      {/* View Button */}
                      <div>
                        <button
                          onClick={() => {
                            setSelectedFeedback(feedback);
                            setShowFeedbackModal(true);
                            // Mark as read when viewing
                            if (!feedback.isRead) {
                              handleMarkAsRead(feedback.id, false);
                            }
                          }}
                          style={{
                            background: 'var(--orange)',
                            color: 'var(--dark)',
                            border: 'none',
                            padding: '0.5rem 1rem',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '0.85rem',
                            fontWeight: '500',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem'
                          }}
                        >
                          <Eye size={14} />
                          View
                        </button>
                      </div>

                      {/* Actions */}
                      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
                        <button
                          onClick={() => handleMarkAsRead(feedback.id, feedback.isRead || false)}
                          disabled={actionLoading === feedback.id}
                          style={{
                            background: feedback.isRead ? '#6b7280' : '#10b981',
                            color: 'white',
                            border: 'none',
                            padding: '0.5rem',
                            borderRadius: '6px',
                            cursor: actionLoading === feedback.id ? 'not-allowed' : 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            opacity: actionLoading === feedback.id ? 0.6 : 1
                          }}
                          title={feedback.isRead ? 'Mark as Unread' : 'Mark as Read'}
                        >
                          {actionLoading === feedback.id ? (
                            <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />
                          ) : feedback.isRead ? (
                            <X size={14} />
                          ) : (
                            <Check size={14} />
                          )}
                        </button>
                        <button
                          onClick={() => handleDeleteFeedback(feedback.id)}
                          disabled={actionLoading === feedback.id}
                          style={{
                            background: 'none',
                            border: '1px solid #ef4444',
                            color: '#ef4444',
                            padding: '0.5rem',
                            borderRadius: '6px',
                            cursor: actionLoading === feedback.id ? 'not-allowed' : 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            opacity: actionLoading === feedback.id ? 0.6 : 1
                          }}
                          title="Delete"
                        >
                          {actionLoading === feedback.id ? (
                            <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />
                          ) : (
                            <Trash2 size={14} />
                          )}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Feedback Detail Modal */}
            {showFeedbackModal && selectedFeedback && (
              <div style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'rgba(0, 0, 0, 0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1000,
                padding: '2rem'
              }}
              onClick={() => setShowFeedbackModal(false)}
              >
                <div style={{
                  background: 'white',
                  borderRadius: '12px',
                  maxWidth: '800px',
                  width: '100%',
                  maxHeight: '90vh',
                  overflowY: 'auto',
                  padding: '2rem',
                  position: 'relative'
                }}
                onClick={(e) => e.stopPropagation()}
                >
                  <button
                    onClick={() => setShowFeedbackModal(false)}
                    style={{
                      position: 'absolute',
                      top: '1rem',
                      right: '1rem',
                      background: 'none',
                      border: 'none',
                      fontSize: '1.5rem',
                      cursor: 'pointer',
                      color: 'var(--gray)'
                    }}
                  >
                    ×
                  </button>

                  <h2 style={{ marginTop: 0, marginBottom: '1.5rem', color: 'var(--dark)' }}>
                    Feedback Details
                  </h2>

                  <div style={{ marginBottom: '1rem', padding: '1rem', background: '#f9fafb', borderRadius: '8px' }}>
                    <div style={{ fontWeight: '600', color: 'var(--dark)', marginBottom: '0.5rem' }}>
                      {selectedFeedback.userEmail || 'Anonymous'}
                    </div>
                    <div style={{ color: 'var(--gray)', fontSize: '0.9rem' }}>
                      Submitted: {new Date(selectedFeedback.submittedAt).toLocaleString()}
                    </div>
                  </div>

                  <div style={{ display: 'grid', gap: '1rem' }}>
                    {(selectedFeedback.courseSatisfaction || selectedFeedback.pdfHelpfulness || selectedFeedback.implementationConfidence) && (
                      <div style={{
                        background: '#f9fafb',
                        padding: '1rem',
                        borderRadius: '8px',
                        border: '1px solid #e5e7eb'
                      }}>
                        <h4 style={{ margin: '0 0 0.75rem 0', color: 'var(--dark)', fontSize: '0.95rem', fontWeight: '600' }}>
                          Course Pulse
                        </h4>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem', fontSize: '0.9rem', color: 'var(--gray)' }}>
                          {selectedFeedback.courseSatisfaction && (
                            <div><strong style={{ color: 'var(--dark)' }}>Course value:</strong> {selectedFeedback.courseSatisfaction}/5</div>
                          )}
                          {selectedFeedback.pdfHelpfulness && (
                            <div><strong style={{ color: 'var(--dark)' }}>Downloadables:</strong> {selectedFeedback.pdfHelpfulness}/5</div>
                          )}
                          {selectedFeedback.implementationConfidence && (
                            <div><strong style={{ color: 'var(--dark)' }}>Implementation confidence:</strong> {selectedFeedback.implementationConfidence}/5</div>
                          )}
                        </div>
                      </div>
                    )}

                    {(selectedFeedback.feedbackTimeliness || selectedFeedback.feedbackQuality || selectedFeedback.feedbackApplicability) && (
                      <div style={{
                        background: '#f9fafb',
                        padding: '1rem',
                        borderRadius: '8px',
                        border: '1px solid #e5e7eb'
                      }}>
                        <h4 style={{ margin: '0 0 0.75rem 0', color: 'var(--dark)', fontSize: '0.95rem', fontWeight: '600' }}>
                          Feedback on Support
                        </h4>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', fontSize: '0.9rem', color: 'var(--gray)' }}>
                          {selectedFeedback.feedbackTimeliness && (
                            <div><strong style={{ color: 'var(--dark)' }}>Timeliness:</strong> {selectedFeedback.feedbackTimeliness}/5</div>
                          )}
                          {selectedFeedback.feedbackQuality && (
                            <div><strong style={{ color: 'var(--dark)' }}>Quality:</strong> {selectedFeedback.feedbackQuality}/5</div>
                          )}
                          {selectedFeedback.feedbackApplicability && (
                            <div><strong style={{ color: 'var(--dark)' }}>Applicability:</strong> {selectedFeedback.feedbackApplicability}/5</div>
                          )}
                        </div>
                      </div>
                    )}

                    {selectedFeedback.highlight && (
                      <div style={{
                        background: '#f9fafb',
                        padding: '1rem',
                        borderRadius: '8px',
                        border: '1px solid #e5e7eb'
                      }}>
                        <h4 style={{ margin: '0 0 0.75rem 0', color: 'var(--dark)', fontSize: '0.95rem', fontWeight: '600' }}>
                          Biggest win so far
                        </h4>
                        <p style={{ margin: 0, fontSize: '0.9rem', color: 'var(--gray)', lineHeight: '1.6' }}>
                          {selectedFeedback.highlight}
                        </p>
                      </div>
                    )}

                    {selectedFeedback.improvementIdeas && (
                      <div style={{
                        background: '#f9fafb',
                        padding: '1rem',
                        borderRadius: '8px',
                        border: '1px solid #e5e7eb'
                      }}>
                        <h4 style={{ margin: '0 0 0.75rem 0', color: 'var(--dark)', fontSize: '0.95rem', fontWeight: '600' }}>
                          Suggested improvements
                        </h4>
                        <p style={{ margin: 0, fontSize: '0.9rem', color: 'var(--gray)', lineHeight: '1.6' }}>
                          {selectedFeedback.improvementIdeas}
                        </p>
                      </div>
                    )}

                    {selectedFeedback.followUpInterest === 'Yes' && (
                      <div style={{
                        background: '#f9fafb',
                        padding: '1rem',
                        borderRadius: '8px',
                        border: '1px solid #e5e7eb'
                      }}>
                        <h4 style={{ margin: '0 0 0.75rem 0', color: 'var(--dark)', fontSize: '0.95rem', fontWeight: '600' }}>
                          Follow-up requested
                        </h4>
                        <p style={{ margin: 0, fontSize: '0.9rem', color: 'var(--gray)', lineHeight: '1.6' }}>
                          Contact: {selectedFeedback.followUpContact || 'Not provided'}
                        </p>
                      </div>
                    )}

                    {selectedFeedback.additionalThoughts && (
                      <div style={{
                        background: '#f9fafb',
                        padding: '1rem',
                        borderRadius: '8px',
                        border: '1px solid #e5e7eb'
                      }}>
                        <h4 style={{ margin: '0 0 0.75rem 0', color: 'var(--dark)', fontSize: '0.95rem', fontWeight: '600' }}>
                          Additional Thoughts
                        </h4>
                        <p style={{ margin: 0, fontSize: '0.9rem', color: 'var(--gray)', lineHeight: '1.6' }}>
                          {selectedFeedback.additionalThoughts}
                        </p>
                      </div>
                    )}

                    {/* Legacy fields fallback */}
                    {(!selectedFeedback.courseSatisfaction && (selectedFeedback.module1ValueRating || selectedFeedback.module2ValueRating || selectedFeedback.module3ValueRating || selectedFeedback.actionableInsights || selectedFeedback.usefulTakeaway)) && (
                      <div style={{
                        background: '#f9fafb',
                        padding: '1rem',
                        borderRadius: '8px',
                        border: '1px solid #e5e7eb'
                      }}>
                        <h4 style={{ margin: '0 0 0.75rem 0', color: 'var(--dark)', fontSize: '0.95rem', fontWeight: '600' }}>
                          Legacy Module Feedback
                        </h4>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', fontSize: '0.9rem', color: 'var(--gray)' }}>
                          {selectedFeedback.module1ValueRating && (
                            <div><strong style={{ color: 'var(--dark)' }}>Module 1 Rating:</strong> {selectedFeedback.module1ValueRating}/5</div>
                          )}
                          {selectedFeedback.module2ValueRating && (
                            <div><strong style={{ color: 'var(--dark)' }}>Module 2 Rating:</strong> {selectedFeedback.module2ValueRating}/5</div>
                          )}
                          {selectedFeedback.module3ValueRating && (
                            <div><strong style={{ color: 'var(--dark)' }}>Module 3 Rating:</strong> {selectedFeedback.module3ValueRating}/5</div>
                          )}
                          {selectedFeedback.actionableInsights && (
                            <div><strong style={{ color: 'var(--dark)' }}>Actionable Insights:</strong> {selectedFeedback.actionableInsights}</div>
                          )}
                          {selectedFeedback.usefulTakeaway && (
                            <div><strong style={{ color: 'var(--dark)' }}>Useful Takeaway:</strong> {selectedFeedback.usefulTakeaway}</div>
                          )}
                        </div>
                      </div>
                    )}

                    {(!selectedFeedback.courseSatisfaction && (selectedFeedback.fieldOfWork || selectedFeedback.upgradeInterest || selectedFeedback.contentFormatPreference || selectedFeedback.trainerConnectionInterest || selectedFeedback.discoverySource || selectedFeedback.recommendationScore)) && (
                      <div style={{
                        background: '#f9fafb',
                        padding: '1rem',
                        borderRadius: '8px',
                        border: '1px solid #e5e7eb'
                      }}>
                        <h4 style={{ margin: '0 0 0.75rem 0', color: 'var(--dark)', fontSize: '0.95rem', fontWeight: '600' }}>
                          Legacy Context
                        </h4>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', fontSize: '0.9rem', color: 'var(--gray)' }}>
                          {selectedFeedback.fieldOfWork && (
                            <div><strong style={{ color: 'var(--dark)' }}>Field of Work:</strong> {selectedFeedback.fieldOfWork}</div>
                          )}
                          {selectedFeedback.upgradeInterest && (
                            <div><strong style={{ color: 'var(--dark)' }}>Upgrade Interest:</strong> {selectedFeedback.upgradeInterest}</div>
                          )}
                          {selectedFeedback.upgradePriceRange && (
                            <div><strong style={{ color: 'var(--dark)' }}>Price Range:</strong> {selectedFeedback.upgradePriceRange}</div>
                          )}
                          {selectedFeedback.contentFormatPreference && (
                            <div><strong style={{ color: 'var(--dark)' }}>Content Format Preference:</strong> {selectedFeedback.contentFormatPreference}</div>
                          )}
                          {selectedFeedback.trainerConnectionInterest && (
                            <div><strong style={{ color: 'var(--dark)' }}>Trainer Connection Interest:</strong> {selectedFeedback.trainerConnectionInterest}</div>
                          )}
                          {selectedFeedback.trainerConnectionType && (
                            <div><strong style={{ color: 'var(--dark)' }}>Connection Type:</strong> {selectedFeedback.trainerConnectionType}</div>
                          )}
                          {selectedFeedback.discoverySource && (
                            <div><strong style={{ color: 'var(--dark)' }}>Discovery Source:</strong> {selectedFeedback.discoverySource}</div>
                          )}
                          {selectedFeedback.recommendationScore && (
                            <div><strong style={{ color: 'var(--dark)' }}>Recommendation Score (0-10):</strong> {selectedFeedback.recommendationScore}</div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}

export default AdminPanel;